<template>
	<div class="create-auto-campaign-wrapper">
		<div class="create-auto-campaign-page">
			<div class="create-campaign-container container">
				<!-- Header -->
				<div class="page-header d-flex align-items-center my-4">
					<router-link :to="{ name: 'autoCampaigns' }" class="back-btn me-3" title="Back to Auto Campaigns">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">Edit Auto Campaign with Triggers</h2>
						<p class="page-subtitle">Update your automated campaign settings with triggers.</p>
					</div>
				</div>
				<div class="d-flex justify-content-end mb-3 gap-2" v-if="campaign.id">
					<button v-if="canTestTemplate" type="button" class="btn btn-outline-secondary btn-sm" @click="openLocalTemplateTestModal">
						<i class="fad fa-vial me-2"></i>
						Test Template
					</button>
					<button v-if="canTestTemplate" type="button" class="btn btn-outline-secondary btn-sm" @click="openLobTemplateTestModal">
						<i class="fad fa-mailbox me-2"></i>
						Test Template LOB
					</button>
				</div>

				<form @submit.prevent="saveCampaign">
					<div class="row">
						<!-- Left Column - Images/PDF Preview -->
						<div class="col-md-5">
							<div class="preview-container">
								<!-- Letter Content Preview -->
								<div v-if="campaign.type === 'letter'" class="letter-content-section">
									<h3 class="preview-title">Letter Content</h3>

									<!-- Content Type Selection -->
									<div class="content-type-selection mb-3">
										<div class="btn-group w-100" role="group">
											<input type="radio" class="btn-check" name="letterContentType" id="contentTypePdf" value="pdf" v-model="campaign.letter_content_type" />
											<label class="btn btn-outline-secondary" for="contentTypePdf">
												<i class="fad fa-file-pdf me-2"></i>
												Upload PDF
											</label>
											<input
												type="radio"
												class="btn-check"
												name="letterContentType"
												id="contentTypeHtml"
												value="html"
												v-model="campaign.letter_content_type"
											/>
											<label class="btn btn-outline-secondary" for="contentTypeHtml">
												<i class="fad fa-code me-2"></i>
												HTML Editor
											</label>
										</div>
									</div>

									<!-- PDF Upload Section -->
									<div v-if="campaign.letter_content_type === 'pdf'" class="pdf-preview-wrapper">
										<div v-if="hasPdf" class="pdf-preview">
											<div class="pdf-viewer-container">
												<div v-if="pdfLoading" class="pdf-loading">
													<i class="fad fa-spinner fa-spin fa-2x"></i>
													<p>Loading PDF...</p>
												</div>
												<div v-else class="pdf-viewer-wrapper">
													<iframe v-if="pdfViewMethod === 'iframe'" :src="pdfViewerUrl" class="pdf-viewer" @load="onPdfLoad" @error="onPdfError"></iframe>
													<object
														v-else-if="pdfViewMethod === 'object'"
														:data="pdfDirectUrl"
														type="application/pdf"
														class="pdf-viewer"
														@load="onPdfLoad"
														@error="onPdfError"
													>
														<embed :src="pdfDirectUrl" type="application/pdf" class="pdf-viewer" />
													</object>
													<div class="pdf-fallback" v-else>
														<div class="fallback-content">
															<i class="fad fa-file-pdf fa-3x pdf-icon"></i>
															<h4>PDF Preview</h4>
															<p class="pdf-name">{{ campaign.letter_pdf_name }}</p>
															<p class="fallback-message">Preview not available. Click below to view the PDF.</p>
															<button type="button" class="btn btn-primary btn-sm" @click="viewPDF">
																<i class="fad fa-external-link-alt me-2"></i>
																Open PDF
															</button>
														</div>
													</div>
												</div>
											</div>
											<div class="pdf-info">
												<span class="pdf-name">{{ campaign.letter_pdf_name }}</span>
											</div>
											<div class="pdf-actions">
												<button type="button" class="btn btn-sm btn-outline-primary" @click="viewPDF">
													<i class="fad fa-external-link-alt"></i> Full View
												</button>
												<button type="button" class="btn btn-sm btn-outline-danger" @click="removePDF"><i class="fad fa-trash"></i> Remove</button>
											</div>
										</div>
										<div v-else class="pdf-upload-placeholder" @click="triggerPDFUpload">
											<div class="upload-content">
												<div class="upload-icon-wrapper">
													<i class="fad fa-cloud-upload-alt fa-3x"></i>
												</div>
												<h4 class="upload-title">Upload PDF File</h4>
												<p class="upload-description">Click here to select a PDF from your computer</p>
												<div class="upload-formats">
													<span class="format-badge">PDF</span>
												</div>
											</div>
										</div>
										<input ref="pdfInput" type="file" accept=".pdf" @change="handlePDFUpload" style="display: none" />
									</div>

									<!-- HTML Editor Section -->
									<div v-if="campaign.letter_content_type === 'html'" class="html-preview-wrapper">
										<div v-if="campaign.letter_html" class="html-preview">
											<div class="html-viewer-container">
												<div class="html-content" v-html="campaign.letter_html"></div>
											</div>
											<div class="html-info">
												<span class="content-length">{{ getContentLength() }} characters</span>
											</div>
											<div class="html-actions">
												<button type="button" class="btn btn-sm btn-outline-primary" @click="openHtmlEditor">
													<i class="fad fa-edit"></i> Edit Content
												</button>
												<button type="button" class="btn btn-sm btn-outline-danger" @click="removeHtmlContent"><i class="fad fa-trash"></i> Remove</button>
											</div>
										</div>
										<div v-else class="html-create-placeholder" @click="openHtmlEditor">
											<div class="upload-content">
												<div class="upload-icon-wrapper">
													<i class="fad fa-file-alt fa-3x"></i>
												</div>
												<h4 class="upload-title">Create Letter Content</h4>
												<p class="upload-description">Click here to create letter content with WYSIWYG editor</p>
												<div class="upload-formats">
													<span class="format-badge">HTML</span>
													<span class="format-badge">Templates</span>
												</div>
											</div>
										</div>
									</div>

									<!-- No Selection Placeholder -->
									<div v-if="!campaign.letter_content_type" class="content-type-placeholder">
										<div class="placeholder-content">
											<i class="fad fa-arrow-up fa-2x arrow-icon"></i>
											<p class="placeholder-message">Select content type above</p>
										</div>
									</div>
								</div>

								<!-- Postcard Images Preview -->
								<div v-if="campaign.type === 'postcard'" class="postcard-preview-section">
									<h3 class="preview-title">Postcard Images</h3>

									<!-- Front Image -->
									<div class="postcard-image-wrapper mb-3">
										<div class="image-header d-flex justify-content-between align-items-center mb-2">
											<h4 class="image-title mb-0">
												Front Image <small class="text-muted">({{ campaign.postcard_size }})</small>
											</h4>
											<div class="template-toggle-container">
												<div class="form-check form-switch">
													<input class="form-check-input" type="checkbox" id="frontImageTemplateToggle" v-model="campaign.front_is_template" />
													<label class="form-check-label" for="frontImageTemplateToggle">Template Image</label>
												</div>
											</div>
										</div>
										<div class="postcard-image-container" :style="{ height: postcardImageHeight + 'px' }">
											<div v-if="campaign.postcard_front" class="postcard-image">
												<img :src="campaign.postcard_front" alt="Postcard Front" />
												<div class="image-overlay">
													<div class="overlay-icon info-icon" @click="showFrontImageInfo">
														<i class="fad fa-info"></i>
													</div>
													<div v-if="campaign.front_is_template" class="overlay-icon template-icon" @click="openTemplateSettings('front')">
														<i class="fad fa-cog"></i>
													</div>
													<div class="overlay-icon delete-icon" @click="removeFrontImage">
														<i class="fad fa-trash"></i>
													</div>
												</div>
											</div>
											<div v-else class="image-upload-placeholder" @click="triggerFrontUpload">
												<i class="fad fa-image fa-3x placeholder-icon"></i>
												<p class="placeholder-text">Click to add front image</p>
												<small class="size-hint">{{ campaign.postcard_size }}</small>
											</div>
										</div>
										<input ref="frontImageInput" type="file" accept="image/*" @change="handleFrontImageUpload" style="display: none" />
									</div>

									<!-- Back Image -->
									<div class="postcard-image-wrapper">
										<div class="image-header d-flex justify-content-between align-items-center mb-2">
											<h4 class="image-title mb-0">
												Back Image <small class="text-muted">({{ campaign.postcard_size }})</small>
											</h4>
											<div class="template-toggle-container">
												<div class="form-check form-switch">
													<input class="form-check-input" type="checkbox" id="backImageTemplateToggle" v-model="campaign.back_is_template" />
													<label class="form-check-label" for="backImageTemplateToggle">Template Image</label>
												</div>
											</div>
										</div>
										<div class="postcard-image-container" :style="{ height: postcardImageHeight + 'px' }">
											<div v-if="campaign.postcard_back" class="postcard-image">
												<img :src="campaign.postcard_back" alt="Postcard Back" />
												<div class="image-overlay">
													<div class="overlay-icon info-icon" @click="showBackImageInfo">
														<i class="fad fa-info"></i>
													</div>
													<div v-if="campaign.back_is_template" class="overlay-icon template-icon" @click="openTemplateSettings('back')">
														<i class="fad fa-cog"></i>
													</div>
													<div class="overlay-icon delete-icon" @click="removeBackImage">
														<i class="fad fa-trash"></i>
													</div>
												</div>
											</div>
											<div v-else class="image-upload-placeholder" @click="triggerBackUpload">
												<i class="fad fa-image fa-3x placeholder-icon"></i>
												<p class="placeholder-text">Click to add back image</p>
												<small class="size-hint">{{ campaign.postcard_size }}</small>
											</div>
										</div>
										<input ref="backImageInput" type="file" accept="image/*" @change="handleBackImageUpload" style="display: none" />
									</div>
								</div>

								<!-- Placeholder when no type selected -->
								<div v-if="!campaign.type" class="type-placeholder">
									<i class="fad fa-arrow-right fa-2x arrow-icon"></i>
									<p class="placeholder-message">Select mail type to see preview</p>
								</div>
							</div>
						</div>

						<!-- Right Column - Form -->
						<div class="col-md-7 form-column">
							<!-- Basic Information Section -->
							<div class="form-section">
								<h2 class="section-title">Basic Information</h2>
								<p class="section-description">General information about your campaign.</p>

								<div class="form-group">
									<label for="campaignName" class="form-label">Campaign name <span class="required">*</span></label>
									<input type="text" class="form-control" id="campaignName" v-model="campaign.name" required />
								</div>

								<div class="form-group">
									<label for="campaignDescription" class="form-label">Campaign description</label>
									<textarea
										class="form-control"
										id="campaignDescription"
										v-model="campaign.description"
										rows="3"
										placeholder="Brief description of the campaign purpose and target audience..."
									></textarea>
								</div>

								<div class="form-group">
									<label for="campaignUtm" class="form-label">UTM campaign</label>
									<utm-campaign-input id="campaignUtm" v-model="campaign.utm_campaign" purl />
								</div>

								<div class="form-group">
									<label for="campaignUtmContent" class="form-label">UTM content</label>
									<utm-content-input id="campaignUtmContent" v-model="campaign.utm_content" />
								</div>

								<div class="form-group">
									<label for="campaignType" class="form-label">Mail type <span class="required">*</span></label>
									<div class="mail-type-selection-inline">
										<div class="form-check form-check-inline">
											<input class="form-check-input" type="radio" name="mailType" id="letterType" value="letter" v-model="campaign.type" />
											<label class="form-check-label" for="letterType">
												<i class="fad fa-file-alt me-2"></i>
												Letter
											</label>
										</div>
										<div class="form-check form-check-inline">
											<input class="form-check-input" type="radio" name="mailType" id="postcardType" value="postcard" v-model="campaign.type" />
											<label class="form-check-label" for="postcardType">
												<i class="fad fa-image me-2"></i>
												Postcard
											</label>
										</div>
									</div>
								</div>
							</div>

							<!-- Campaign Mode Section -->
							<div class="form-section">
								<h2 class="section-title">Campaign Mode</h2>
								<p class="section-description">Choose between test and live mode and mail service.</p>

								<div class="row">
									<div class="col-md-6">
										<div class="form-group">
											<label class="form-label">Mode <span class="required">*</span></label>
											<div class="mode-selection-inline">
												<div class="form-check form-check-inline">
													<input class="form-check-input" type="radio" name="mode" id="testMode" value="test" v-model="campaign.mode" />
													<label class="form-check-label" for="testMode">
														<strong>Test Campaign</strong>
														<small class="d-block text-muted">Safe testing environment with reduced costs</small>
													</label>
												</div>
												<div class="form-check form-check-inline">
													<input class="form-check-input" type="radio" name="mode" id="liveMode" value="live" v-model="campaign.mode" />
													<label class="form-check-label" for="liveMode">
														<strong>Live Campaign</strong>
														<small class="d-block text-muted">Production environment with full functionality</small>
													</label>
												</div>
											</div>
										</div>
									</div>
									<div class="col-md-6">
										<div class="form-group">
											<label for="mailService" class="form-label">Mail Service <span class="required">*</span></label>
											<select class="form-select" id="mailService" v-model="campaign.mail_type" required>
												<option value="">Select Mail Service</option>
												<option value="usps_first_class">USPS First Class</option>
												<option value="usps_standard">USPS Standard</option>
											</select>
										</div>
									</div>
								</div>
							</div>

							<!-- Delivery Settings Section -->
							<div v-if="campaign.type" class="form-section">
								<h2 class="section-title">{{ campaign.type === 'letter' ? 'Letter' : 'Postcard' }} Settings</h2>
								<p class="section-description">Configure settings specific to {{ campaign.type }} delivery.</p>

								<!-- Letter Settings -->
								<div v-if="campaign.type === 'letter'">
									<div class="settings-grid">
										<div class="form-group">
											<label for="letterSender" class="form-label">Campaign Sender <span class="required">*</span></label>
											<select class="form-select" id="letterSender" v-model="campaign.sender" required>
												<option value="">Select Sender</option>
												<option v-for="sender in senders" :key="sender.name" :value="sender.name">
													{{ sender.name }}
												</option>
											</select>
										</div>
										<div class="form-group">
											<label for="letterColor" class="form-label">Color <span class="required">*</span></label>
											<select class="form-select" id="letterColor" v-model="campaign.letter_color" required>
												<option value="">Select</option>
												<option value="1">Yes</option>
												<option value="0">No</option>
											</select>
										</div>
										<div class="form-group">
											<label for="letterDoubleSided" class="form-label">Double sided <span class="required">*</span></label>
											<select class="form-select" id="letterDoubleSided" v-model="campaign.letter_double_sided" required>
												<option value="">Select</option>
												<option value="1">Yes</option>
												<option value="0">No</option>
											</select>
										</div>
										<div class="form-group">
											<label for="letterExtraService" class="form-label">Extra service</label>
											<select class="form-select" id="letterExtraService" v-model="campaign.letter_extra_service">
												<option value="">None</option>
												<option value="certified">Certified</option>
												<option value="certified_return_receipt">Certified return receipt</option>
											</select>
										</div>
									</div>
									<div class="form-group">
										<label for="letterAddressPlacement" class="form-label">Address placement <span class="required">*</span></label>
										<select class="form-select" id="letterAddressPlacement" v-model="campaign.letter_address_placement" required>
											<option value="">Select placement</option>
											<option value="top_first_page">On top of first page</option>
											<option value="insert_blank_page">On a blank page</option>
										</select>
									</div>
									<div class="settings-grid">
										<div class="form-group">
											<label for="letterType" class="form-label">Letter Type</label>
											<select class="form-select" id="letterType" v-model="campaign.letter_type">
												<option value="default">Default</option>
												<option value="single-item-pattern">Single Item Pattern</option>
											</select>
										</div>
										<div class="form-group">
											<label for="letterPagesCount" class="form-label">Letter Pages Count</label>
											<input type="number" class="form-control" id="letterPagesCount" v-model="campaign.letter_pages_count" min="1" max="10" />
											<small class="form-text text-muted">Just for calculation</small>
										</div>
									</div>
								</div>

								<!-- Postcard Settings -->
								<div v-if="campaign.type === 'postcard'">
									<div class="settings-grid">
										<div class="form-group">
											<label for="postcardSize" class="form-label">Size <span class="required">*</span></label>
											<select class="form-select" id="postcardSize" v-model="campaign.postcard_size" required>
												<option value="">Select Size</option>
												<option value="4x6">4x6</option>
												<option value="6x9">6x9</option>
												<option value="6x11">6x11</option>
											</select>
										</div>
										<div class="form-group">
											<label for="postcardSender" class="form-label">Campaign Sender <span class="required">*</span></label>
											<select class="form-select" id="postcardSender" v-model="campaign.sender" required>
												<option value="">Select Sender</option>
												<option v-for="sender in senders" :key="sender.name" :value="sender.name">
													{{ sender.name }}
												</option>
											</select>
										</div>
									</div>
								</div>
							</div>

							<!-- Trigger Settings Section -->
							<div class="form-section">
								<h2 class="section-title">Trigger Settings</h2>
								<p class="section-description">Configure when this campaign should be triggered.</p>

								<div class="form-group">
									<label for="triggerType" class="form-label">Trigger type <span class="required">*</span></label>
									<select class="form-select" id="triggerType" v-model="campaign.trigger.type" required>
										<option value="">Select Trigger</option>
										<option v-for="trigger in triggers" :key="trigger.id" :value="trigger.id">
											{{ trigger.name }}
										</option>
									</select>
								</div>

								<div class="form-group">
									<label class="form-label">Status</label>
									<div class="status-selection">
										<div class="form-check" :class="{ 'status-active': campaign.status === 'active' }">
											<input class="form-check-input" type="radio" name="status" id="statusActive" value="active" v-model="campaign.status" />
											<label class="form-check-label" for="statusActive">
												<strong>Active</strong>
												<small class="d-block text-muted">Campaign will start automatically when triggered</small>
											</label>
										</div>
										<div class="form-check" :class="{ 'status-draft': campaign.status === 'draft' }">
											<input class="form-check-input" type="radio" name="status" id="statusDraft" value="draft" v-model="campaign.status" />
											<label class="form-check-label" for="statusDraft">
												<strong>Draft</strong>
												<small class="d-block text-muted">Campaign will be saved but not activated</small>
											</label>
										</div>
										<div class="form-check" :class="{ 'status-paused': campaign.status === 'paused' }">
											<input class="form-check-input" type="radio" name="status" id="statusPaused" value="paused" v-model="campaign.status" />
											<label class="form-check-label" for="statusPaused">
												<strong>Paused</strong>
												<small class="d-block text-muted">Campaign will be temporarily disabled</small>
											</label>
										</div>
									</div>
								</div>

								<!-- Pause Duration (shown only when paused is selected) -->
								<div v-if="campaign.status === 'paused'" class="form-group pause-duration-group">
									<label for="pauseDuration" class="form-label">Pause Duration <span class="required">*</span></label>
									<select class="form-select" id="pauseDuration" v-model="campaign.pause_duration" @change="calculatePauseUntil" required>
										<option value="">Select pause duration</option>
										<option value="7_days">7 days</option>
										<option value="2_weeks">2 weeks</option>
										<option value="1_month">1 month</option>
										<option value="3_months">3 months</option>
										<option value="6_months">6 months</option>
										<option value="1_year">1 year</option>
										<option value="indefinite">Indefinite (manual activation required)</option>
									</select>
									<small v-if="campaign.pause_until && campaign.pause_duration !== 'indefinite'" class="form-text text-muted">
										Campaign will automatically resume on {{ formatPauseUntilDate }}
									</small>
								</div>
							</div>

							<div class="form-section unique-url-section">
								<div class="d-flex justify-content-between align-items-start">
									<div><h2 class="section-title">Unique URL tracking</h2><p class="section-description">Every run item gets its own short link. <code>{unique_url}</code> prints it (<code>twn.link/a7k2m</code>) and <code>{unique_url_qr}</code> prints a QR code for it. The short link records the first visit and redirects to the destination URL with UTM params, so scans and typed visits are reported separately.</p></div>
									<div class="form-check form-switch mt-1"><input class="form-check-input" type="checkbox" id="uniqueUrlEnabled" v-model="campaign.unique_url_enabled" /><label class="form-check-label" for="uniqueUrlEnabled">Enabled</label></div>
								</div>
								<div v-if="campaign.unique_url_enabled">
									<div class="form-group"><label class="form-label">Destination URL <small class="text-muted">(optional)</small></label><input type="url" class="form-control" v-model="campaign.unique_url_domain" placeholder="https://townweb.com" /><small class="form-text text-muted">Where the short link sends visitors (UTM params are added). Leave empty to use the selected trigger's destination: {{ selectedTriggerUniqueUrlDomain || 'not configured' }}.</small></div>
									<div class="form-group mt-2"><label class="form-label" for="autoShortlinkDomain">Short domain</label><shortlink-domain-select id="autoShortlinkDomain" v-model="campaign.shortlink_domain" /><button type="button" class="btn btn-sm btn-outline-secondary mt-2" :disabled="applyingDomain || !campaignId" @click="applyDomainToUnsent"><i :class="['fad', 'me-1', applyingDomain ? 'fa-spinner fa-spin' : 'fa-sync']"></i> Apply to prepared run items</button><small class="form-text text-muted d-block">Saves the campaign, moves links of not-yet-mailed (prepared) run items to this domain and re-renders their postcards.</small></div>
									<div class="form-check form-switch mt-2"><input class="form-check-input" type="checkbox" id="shortlinkSlackNotify" v-model="campaign.shortlink_slack_notify" /><label class="form-check-label" for="shortlinkSlackNotify">Slack notification on every visit</label><small class="form-text text-muted d-block">Posts to the channel set in Settings → Short Links. Can also be switched in the Response Report.</small></div>
								</div>
							</div>

							<!-- Email Settings Section -->
							<div class="form-section">
								<div class="d-flex justify-content-between align-items-start">
									<div>
										<h2 class="section-title">Email Channel</h2>
										<p class="section-description">Enable email delivery for this trigger campaign and define provider, template and scheduling.</p>
									</div>
									<div class="form-check form-switch mt-1">
										<input class="form-check-input" type="checkbox" id="emailEnabled" v-model="campaign.email_config.enabled" />
										<label class="form-check-label" for="emailEnabled">Enabled</label>
									</div>
								</div>

								<div v-if="campaign.email_config.enabled">
									<div class="settings-grid">
										<div class="form-group">
											<label class="form-label">Email Provider <span class="required">*</span></label>
											<select class="form-select" v-model="campaign.email_config.email_provider_config_id">
												<option :value="null">Select Provider</option>
												<option v-for="provider in emailProviderConfigs" :key="provider.id" :value="provider.id">
													{{ provider.name }} ({{ provider.provider }})
												</option>
											</select>
										</div>
										<div class="form-group">
											<label class="form-label">Email Template <span class="required">*</span></label>
											<select class="form-select" v-model="campaign.email_config.email_template_id">
												<option :value="null">Select Template</option>
												<option v-for="template in emailTemplates" :key="template.id" :value="template.id">
													{{ template.name }}
												</option>
											</select>
										</div>
									</div>

									<div class="form-group">
										<label class="form-label">Recipient Source <span class="required">*</span></label>
										<div class="recipient-source-display">
											<div class="recipient-source-card">
												<div class="recipient-source-label">{{ currentRecipientSourceLabel }}</div>
												<small class="text-muted">Choose whether the email goes to municipality email, all contact emails, or both.</small>
											</div>
											<button type="button" class="btn btn-outline-primary btn-sm" @click="openRecipientSourceModal">
												Choose Source
											</button>
										</div>
									</div>

									<div class="settings-grid">
										<div class="form-group">
											<label class="form-label">Schedule Type <span class="required">*</span></label>
											<select class="form-select" v-model="campaign.email_config.schedule_type">
												<option value="immediate">Immediately when run is created</option>
												<option value="delay_after_run_created">Delay from run creation</option>
												<option value="after_postcard_sent">After postcard is sent</option>
												<option value="after_letter_sent">After letter is sent</option>
											</select>
										</div>
										<div class="form-group" v-if="campaign.email_config.schedule_type === 'delay_after_run_created'">
											<label class="form-label">Delay</label>
											<div class="d-flex gap-2">
												<input type="number" class="form-control" min="0" v-model.number="campaign.email_config.delay_value" />
												<select class="form-select" v-model="campaign.email_config.delay_unit">
													<option value="minutes">Minutes</option>
													<option value="hours">Hours</option>
													<option value="days">Days</option>
												</select>
											</div>
										</div>
									</div>
								</div>
							</div>

							<!-- Action Buttons -->
							<div class="form-actions">
								<div class="pricing-display">
									<span v-if="campaign.type && campaign.mail_type" class="pricing-info">
										<span class="price-value">${{ pricePerItem }}</span> item
									</span>
								</div>
								<div class="action-buttons">
									<router-link :to="{ name: 'autoCampaigns' }" class="btn btn-secondary">Cancel</router-link>
									<button type="submit" class="btn btn-primary" :disabled="saving">
										<i v-if="saving" class="fad fa-spinner fa-spin me-2"></i>
										{{ saving ? (isEditing ? 'Updating Campaign...' : 'Creating Campaign...') : isEditing ? 'Update Campaign' : 'Create Campaign' }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</form>
			</div>

			<div class="modal fade" id="recipientSourceModal" tabindex="-1" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Choose Recipient Source</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<div
								v-for="option in recipientSourceOptions"
								:key="option.value"
								class="recipient-option"
								:class="{ active: campaign.email_config.recipient_source === option.value }"
								@click="setRecipientSource(option.value)"
							>
								<div class="form-check mb-0">
									<input class="form-check-input" type="radio" :checked="campaign.email_config.recipient_source === option.value" />
									<label class="form-check-label">
										<strong>{{ option.label }}</strong>
										<small class="d-block text-muted">{{ option.description }}</small>
									</label>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Image Info Modal -->
			<div class="modal fade" id="imageInfoModal" tabindex="-1" aria-labelledby="imageInfoModalLabel" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title" id="imageInfoModalLabel">Image Information</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
								<i class="fad fa-times"></i>
							</button>
						</div>
						<div class="modal-body">
							<div class="image-preview mb-3">
								<img :src="selectedImageInfo.src" :alt="selectedImageInfo.name" class="modal-image" />
							</div>
							<div class="image-details">
								<div class="detail-row">
									<span class="detail-label">File Name:</span>
									<span class="detail-value">{{ selectedImageInfo.name }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">File Size:</span>
									<span class="detail-value">{{ selectedImageInfo.size }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">Dimensions:</span>
									<span class="detail-value">{{ selectedImageInfo.dimensions }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">File Type:</span>
									<span class="detail-value">{{ selectedImageInfo.type }}</span>
								</div>
								<div class="detail-row">
									<span class="detail-label">Postcard Size:</span>
									<span class="detail-value">{{ campaign.postcard_size }}</span>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Template Test Preview Modal -->
			<div class="modal fade" id="templateTestModal" tabindex="-1" aria-labelledby="templateTestModalLabel" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered modal-lg">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title" id="templateTestModalLabel">{{ templateTest.mode === 'lob' ? 'Test Template LOB' : 'Test Template' }}</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeTemplateTestModal">
								<i class="fad fa-times"></i>
							</button>
						</div>
						<div class="modal-body text-center">
							<div v-if="templateTest.loading" class="py-5">
								<div class="spinner-border text-primary" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
								<p class="mt-3 mb-0">{{ templateTest.mode === 'lob' ? 'Sending to LOB and waiting 15 seconds for the proof…' : 'Generating local template preview…' }}</p>
							</div>
							<div v-else-if="templateTest.error" class="py-4 text-danger">
								<i class="fad fa-exclamation-triangle me-2"></i>{{ templateTest.error }}
							</div>
							<div v-else>
								<div v-if="templateTest.thumbnails.length" class="d-flex flex-wrap justify-content-center gap-3">
									<div v-for="(thumb, i) in templateTest.thumbnails" :key="i">
										<img :src="thumb" alt="Postcard preview" class="img-fluid border rounded" style="max-height: 360px" />
									</div>
								</div>
								<p v-else class="text-muted py-4">No template preview was generated.</p>
								<a v-if="templateTest.url" :href="templateTest.url" target="_blank" rel="noopener" class="btn btn-link btn-sm mt-2">
									<i class="fad fa-file-pdf me-1"></i> Open full proof (PDF)
								</a>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" @click="closeTemplateTestModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Template Settings Modal -->
			<div class="modal fade" id="templateSettingsModal" tabindex="-1" aria-labelledby="templateSettingsModalLabel" aria-hidden="true">
				<div class="modal-dialog modal-fullscreen-lg-down modal-dialog-centered template-modal">
					<div class="modal-content">
						<div class="modal-header pattern-modal-header justify-content-start">
							<div>
								<h5 class="modal-title pattern-modal-title" id="templateSettingsModalLabel">Template pattern editor</h5>
								<small class="pattern-modal-sub">Place dynamic text on the {{ currentTemplateSide === 'front' ? 'front' : 'back' }} image. Click the canvas to add it, then drag to fine-tune.</small>
							</div>
							<button type="button" class="pattern-modal-close" data-bs-dismiss="modal" aria-label="Close"><span aria-hidden="true">×</span></button>
						</div>
						<div class="modal-body template-modal-body">
							<!-- Template Image Container (Full Size) -->
							<div class="template-image-main-container">
								<div class="template-image-scroll-container">
									<div class="template-image-container" @click="handleImageClick" ref="templateImageContainer">
										<img :src="getCurrentTemplateImage()" :alt="currentTemplateSide + ' template'" class="template-preview-image" ref="templateImage" @load="onTemplateImageLoad" />
										<!-- Text Pattern Overlays -->
										<div
											v-for="(pattern, index) in getCurrentPatterns()"
											:key="index"
											class="text-pattern-overlay"
											:style="{
												left: pattern.x + '%',
												top: pattern.y + '%',
											fontSize: getDisplayFontSize(pattern.fontSize) + 'px',
												color: pattern.color,
											fontFamily: getPreviewFontFamily(pattern.fontFamily),
												fontWeight: pattern.fontWeight,
												WebkitTextStroke: getPreviewTextStroke(pattern),
											}"
											@click.stop="selectPattern(index)"
										:class="{ selected: selectedPatternIndex === index, dragging: dragState.isDragging && dragState.patternIndex === index, 'legacy-coordinate-pattern': (pattern.coordinateVersion || 1) < 2 && pattern.text !== '{unique_url_qr}' }"
										>
											<div
												class="pattern-drag-handle"
												@mousedown="startDrag($event, index)"
												@mouseenter="showDragHandle(index)"
												@mouseleave="hideDragHandle"
												:class="{ visible: hoveredPatternIndex === index || selectedPatternIndex === index }"
											>
												<i class="fad fa-arrows-alt"></i>
											</div>
											<span v-if="pattern.text === '{unique_url_qr}'" class="pattern-qr-preview" :style="{ width: getDisplayFontSize(pattern.qrSize || 220) + 'px', height: getDisplayFontSize(pattern.qrSize || 220) + 'px' }"><i class="fas fa-qrcode"></i></span>
											<span v-else class="pattern-text-content">{{ getPatternPreviewText(pattern.text) }}</span>
										</div>
									</div>
								</div>
							</div>

							<!-- Pattern Configuration Sidebar -->
							<div class="template-sidebar">
								<div class="pattern-sidebar-heading">Pattern configuration</div>
								<div class="pattern-sidebar-sub">Build the content, choose variables, then position it on the image.</div>

								<!-- Add New Pattern -->
								<div class="pattern-section mb-3">
									<label class="form-label">Add Pattern Text</label>
									<div class="input-group mb-2">
										<input type="text" class="form-control" v-model="newPatternText" placeholder="e.g. Hello {name} from {city}, {state}" />
										<button class="btn btn-primary input-group-btn" type="button" @click="addPattern">
											<i class="fad fa-plus"></i>
										</button>
									</div>
									<div class="placement-hint"><i class="fad fa-mouse-pointer me-2"></i>Type text, then click the image to place it exactly there. You can drag it afterwards.</div>
									<div class="variable-helper">
										<div class="variable-helper-title"><i class="fad fa-brackets-curly me-2"></i>Available variables</div>
										<button v-for="variable in patternVariables" :key="variable.token" type="button" class="variable-chip" @click="insertPatternVariable(variable.token)">
											<code>{{ variable.token }}</code><span>{{ variable.description }}</span>
										</button>
									</div>
								</div>

								<!-- Pattern List -->
								<div v-if="getCurrentPatterns().length > 0" class="pattern-list mb-3">
									<label class="form-label">Existing Patterns</label>
									<div
										v-for="(pattern, index) in getCurrentPatterns()"
										:key="index"
										class="pattern-item"
										:class="{ selected: selectedPatternIndex === index }"
										@click="selectPattern(index)"
									>
										<div class="pattern-text"><strong>{{ pattern.text }}</strong><small>X {{ pattern.x.toFixed(1) }}% · Y {{ pattern.y.toFixed(1) }}%</small></div>
										<div class="pattern-actions">
											<button class="btn btn-sm btn-outline-danger" @click.stop="removePattern(index)">
												<i class="fad fa-trash"></i>
											</button>
										</div>
									</div>
								</div>

								<!-- Selected Pattern Styling -->
								<div v-if="selectedPatternIndex !== null" class="pattern-styling">
									<h6>Style Selected Pattern</h6>
									<div class="form-group mb-2">
										<label class="form-label">{{ selectedPattern.text === '{unique_url_qr}' ? 'QR Size' : 'Font Size' }}</label>
										<input v-if="selectedPattern.text === '{unique_url_qr}'" type="range" class="form-range" min="80" max="500" v-model="selectedPattern.qrSize" />
										<input v-else type="range" class="form-range" min="24" max="120" v-model="selectedPattern.fontSize" />
										<small class="form-text">{{ selectedPattern.text === '{unique_url_qr}' ? selectedPattern.qrSize : selectedPattern.fontSize }}px</small>
									</div>

									<div class="form-group mb-2">
										<label class="form-label">Font Weight</label>
										<select class="form-select" v-model="selectedPattern.fontWeight">
											<option value="400">Normal</option>
											<option value="500">Medium</option>
											<option value="600">Semi Bold</option>
											<option value="700">Bold</option>
											<option value="800">Extra Bold</option>
											<option value="900">Black</option>
										</select>
									</div>

									<div class="form-group mb-2">
										<label class="form-label">Color</label>
										<input type="color" class="form-control form-control-color" v-model="selectedPattern.color" />
									</div>

									<div class="form-group mb-2">
										<label class="form-label">Font Family</label>
										<select class="form-select" v-model="selectedPattern.fontFamily">
											<option v-for="font in templateFonts" :key="font.value" :value="font.value">{{ font.label }}</option>
										</select>
									</div>

									<button type="button" class="pattern-remove-btn" @click="removePattern(selectedPatternIndex)">
										<i class="fad fa-trash me-2"></i>Remove pattern
									</button>
								</div>

								<!-- Footer Actions -->
								<div class="sidebar-footer mt-4 pt-3 border-top">
									<div class="d-flex gap-2">
										<button type="button" class="pattern-modal-btn flex-fill" data-bs-dismiss="modal">Close</button>
										<button type="button" class="pattern-modal-btn pattern-modal-btn--primary flex-fill" @click="saveTemplateSettings">Save settings</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- HTML Editor Modal -->
		<div class="modal fade" id="htmlEditorModal" tabindex="-1" aria-labelledby="htmlEditorModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-xl modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="htmlEditorModalLabel">
							<i class="fad fa-file-alt me-2"></i>
							Letter Content Editor
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
							<i class="fad fa-times"></i>
						</button>
					</div>
					<div class="modal-body">
						<div class="row">
							<!-- TinyMCE Editor -->
							<div class="col-md-9">
								<Editor v-model="campaign.letter_html" :init="tinymceConfig" ref="tinymceEditor" />
							</div>

							<!-- Template Variables Sidebar -->
							<div class="col-md-3">
								<div class="variables-panel">
									<h6 class="variables-title">
										<i class="fad fa-tags me-2 text-primary"></i>
										Available Variables
									</h6>
									<p class="variables-help">Click to insert:</p>
									<div class="variables-list-wrapper">
										<div
											v-for="(variable, index) in templateVariables"
											:key="`var-${index}`"
											class="variable-item"
											@click="insertVariable(variable)"
											:title="`Click to insert ${variable}`"
										>
											{{ variable }}
										</div>
									</div>
									<div class="variables-help-note">
										<i class="fad fa-info-circle me-2 text-info"></i>
										<small class="text-muted">
											Variables will be replaced with actual values when the letter is sent.
										</small>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer editor-modal-footer">
						<div class="d-flex justify-content-between align-items-center w-100">
							<div class="footer-help">
								<div class="d-flex align-items-center gap-3">
									<small class="text-muted d-flex align-items-center">
										<i class="fad fa-keyboard me-2"></i>
										Use Ctrl+S to save quickly
									</small>
								</div>
							</div>
							<div class="footer-actions">
								<button type="button" class="btn btn-light me-3" data-bs-dismiss="modal">
									<i class="fad fa-times me-2"></i>
									Cancel
								</button>
								<button type="button" class="btn btn-primary btn-lg px-4" @click="saveHtmlContent">
									<i class="fad fa-save me-2"></i>
									Save Letter Content
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index'
import { Modal } from 'bootstrap'
import Editor from '@tinymce/tinymce-vue'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import UtmContentInput from '@/components/common/UtmContentInput'
import ShortlinkDomainSelect from '@/components/campaign/ShortlinkDomainSelect.vue'
import { normalizeUtmCampaign } from '@/utils/utm'
import { DEFAULT_TEMPLATE_FONTS, loadTemplateFonts, patternTextStroke, previewFontFamily } from '@/utils/postcardPatterns'
import { mapState } from 'vuex'

export default {
	name: 'AutoCampaignWithTriggersEdit',
	components: {
		Editor,
		UtmCampaignInput,
		UtmContentInput,
		ShortlinkDomainSelect,
	},
	data() {
		return {
			templateFonts: DEFAULT_TEMPLATE_FONTS,
			saving: false,
			isEditing: false,
			applyingDomain: false,
			campaignId: null,
			triggers: [], // Store available triggers from database
			imageModal: null,
			templateModal: null,
			htmlEditorModal: null,
			selectedImageInfo: {
				src: '',
				name: '',
				size: '',
				dimensions: '',
				type: '',
			},
			frontImageFile: null,
			backImageFile: null,
			pdfFile: null,
			pdfLoading: false,
			pdfViewMethod: 'iframe', // 'iframe', 'object', 'fallback'
			// Custom Editor settings
			editorContent: '',
			savedRange: null,
			showHtmlCode: false,
			// TinyMCE configuration
			tinymceConfig: {
				height: 400,
				menubar: false,
				plugins: 'link code image lists table',
				toolbar:
					'fontselect fontsizeselect formatselect | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | link image table | removeformat code',
				content_style: 'body { font-family: Times New Roman, serif; font-size: 14px; line-height: 1.6; }',
				branding: false,
				elementpath: false,
				statusbar: false,
			},
			// Template variables for letter content
			templateVariables: [
				// Client Info
				'{{ $client.name }}',
				'{{ $client.email }}',
				'{{ $client.phone }}',
				'{{ $client.address }}',
				'{{ $client.company }}',
				'{{ $client.title }}',
				// Municipality Info
				'{{ $municipality.name }}',
				'{{ $municipality.state }}',
				'{{ $municipality.zip }}',
				'{{ $municipality.address }}',
				'{{ $municipality.phone }}',
				'{{ $municipality.website }}',
				// Campaign Info
				'{{ $campaign.name }}',
				'{{ $sender.name }}',
				'{{ $date }}',
				'{{ $time }}',
				'{{ $year }}',
				'{{ $month }}',
				// Service Info
				'{{ $service.name }}',
				'{{ $trial_days }}',
				'{{ $support.email }}',
				'{{ $support.phone }}',
			],
			// Template settings
			currentTemplateSide: null, // 'front' or 'back'
			selectedPatternIndex: null,
			newPatternText: '',
			patternVariables: [
				{ token: '{name}', description: 'Municipality full name' },
				{ token: '{address}', description: 'Mailing address' },
				{ token: '{city}', description: 'City' },
				{ token: '{state}', description: 'State abbreviation' },
				{ token: '{zip}', description: 'ZIP code' },
				{ token: '{country}', description: 'Country' },
				{ token: '{description}', description: 'Campaign description (item-based patterns)' },
				{ token: '{unique_url}', description: 'Unique tracked link for this recipient' },
				{ token: '{unique_url_qr}', description: 'QR code that opens the unique tracked link' },
			],
			hoveredPatternIndex: null,
			dragState: {
				isDragging: false,
				patternIndex: null,
				startX: 0,
				startY: 0,
				initialPatternX: 0,
				initialPatternY: 0,
			},
			campaign: {
				id: null,
				name: '',
				description: '',
				utm_campaign: '',
				utm_content: '',
				type: '',
				mode: '',
				status: 'draft',
				pause_duration: '',
				pause_until: null,
				mail_type: '',
				item_price: 0,
				// Letter fields
				sender: '',
				letter_color: '',
				letter_double_sided: '',
				letter_extra_service: '',
				letter_address_placement: '',
				letter_type: 'default',
				letter_pages_count: 1,
				letter_content_type: 'pdf', // 'pdf' or 'html'
				letter_html: '',
				letter_pdf: null,
				letter_pdf_name: '',
				// Postcard fields
				postcard_size: '4x6',
				postcard_front: null,
				postcard_back: null,
				front_is_template: false,
				back_is_template: false,
				front_patterns: [],
				back_patterns: [],
				unique_url_enabled: false,
				unique_url_domain: '',
				shortlink_slack_notify: false,
				shortlink_domain: null,
				// Trigger settings
				trigger: {
					type: '',
					max_per_day: 100,
				},
				email_config: {
					enabled: false,
					email_template_id: null,
					email_provider_config_id: null,
					recipient_source: 'municipality_email_only',
					schedule_type: 'immediate',
					delay_value: 0,
					delay_unit: 'hours',
					depends_on_channel: null,
					depends_on_event: null,
				},
			},
			// Image dimensions for scaling calculations
			imageDimensions: {
				front: { actual: null, display: null },
				back: { actual: null, display: null },
			},
			// Template test preview state
			templateTest: {
				mode: 'local',
				loading: false,
				error: null,
				id: null,
				url: null,
				thumbnails: [],
				files: [],
			},
			templateTestRequestId: 0,
			// Senders from LOB settings
			senders: [],
			emailTemplates: [],
			emailProviderConfigs: [],
			recipientSourceModal: null,
			recipientSourceOptions: [
				{
					value: 'municipality_email_only',
					label: 'Municipality Email Only',
					description: 'Use only the main email stored on the municipality record.',
				},
				{
					value: 'contacts_only',
					label: 'All Contact Emails',
					description: 'Use emails from municipality contacts only.',
				},
				{
					value: 'municipality_plus_contacts',
					label: 'Municipality + Contacts',
					description: 'Use the municipality email plus all contact emails, deduplicated.',
				},
			],
		}
	},
	computed: {
		...mapState(['authUser']),
		currentRecipientSourceLabel() {
			const option = this.recipientSourceOptions.find(item => item.value === this.campaign.email_config.recipient_source)
			return option ? option.label : 'Municipality Email Only'
		},
		estimatedMunicipalities() {
			return 500
		},
		estimatedCost() {
			const costPerItem = this.campaign.type === 'letter' ? 0.85 : 0.65
			const baseCost = this.estimatedMunicipalities * costPerItem
			const finalCost = this.campaign.mode === 'test' ? baseCost * 0.5 : baseCost
			return finalCost.toFixed(2)
		},
		sendFrequency() {
			if (this.campaign.trigger.type) {
				return this.getTriggerDisplayName(this.campaign.trigger.type)
			}
			return 'Not set'
		},
		postcardAspectRatio() {
			// Postcard dimensions: width x height (in inches)
			const ratios = {
				'4x6': 4 / 6, // 0.667 - Standard postcard (portrait, 4" wide × 6" tall)
				'6x9': 6 / 9, // 0.667 - Large postcard (portrait, 6" wide × 9" tall)
				'6x11': 6 / 11, // 0.545 - Jumbo postcard (portrait, 6" wide × 11" tall)
			}
			return ratios[this.campaign.postcard_size] || ratios['4x6']
		},
		postcardImageHeight() {
			// Use fixed heights for better visual distinction between sizes
			const heights = {
				'4x6': 350, // Standard postcard - medium height
				'6x9': 400, // Large postcard - taller
				'6x11': 480, // Jumbo postcard - tallest
			}

			return heights[this.campaign.postcard_size] || heights['4x6']
		},
		pricePerItem() {
			if (!this.campaign.type || !this.campaign.mail_type) {
				return '0.00'
			}

			let total = 0
			let additionalPageCost = 0

			// LOB Pricing (based on campaigns edit screen)
			const pricing = {
				postcard: {
					'4x6': { usps_first_class: 0.513, usps_standard: 0.513 },
					'6x9': { usps_first_class: 0.554, usps_standard: 0.554 },
					'6x11': { usps_first_class: 0.781, usps_standard: 0.781 },
				},
				letter: {
					black: { usps_first_class: 0.588, usps_standard: 0.548 },
					color: { usps_first_class: 0.761, usps_standard: 0.578 },
					additionalBlack: { usps_first_class: 0.1, usps_standard: 0.1 },
					additionalColor: { usps_first_class: 0.2, usps_standard: 0.2 },
				},
			}

			if (this.campaign.type === 'letter') {
				// Fix color type determination: '1' means Yes (color), '0' means No (black)
				const colorType = this.campaign.letter_color === '1' ? 'color' : 'black'
				total = pricing.letter[colorType][this.campaign.mail_type] || 0
				additionalPageCost = pricing.letter[`additional${colorType.charAt(0).toUpperCase() + colorType.slice(1)}`][this.campaign.mail_type] || 0

				// Extra services
				if (this.campaign.letter_extra_service === 'certified') {
					total += 5.75
				} else if (this.campaign.letter_extra_service === 'certified_return_receipt') {
					total += 5.75 + 2.2
				}

				// Calculate pages cost
				let pages = this.campaign.letter_pages_count || 1
				if (this.campaign.letter_double_sided === '1') {
					pages = Math.ceil(pages / 2)
				}
				const additionalPagesCost = (pages - 1) * additionalPageCost
				total += additionalPagesCost
			} else if (this.campaign.type === 'postcard') {
				total = pricing.postcard[this.campaign.postcard_size][this.campaign.mail_type] || 0
			}

			return total.toFixed(3)
		},
		mailServiceName() {
			const services = {
				usps_first_class: 'USPS First Class',
				usps_standard: 'USPS Standard',
			}
			return services[this.campaign.mail_type] || 'Not selected'
		},
		letterExtraServiceName() {
			const services = {
				certified: 'Certified Mail',
				certified_return_receipt: 'Certified Mail with Return Receipt',
			}
			return services[this.campaign.letter_extra_service] || 'None'
		},
		formatPauseUntilDate() {
			if (!this.campaign.pause_until) {
				return ''
			}
			const date = new Date(this.campaign.pause_until)
			return date.toLocaleDateString('en-US', {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
		pdfViewerUrl() {
			if (!this.campaign.letter_pdf) {
				return ''
			}
			// For uploaded files (data URLs), use them directly
			return this.campaign.letter_pdf
		},
		hasPdf() {
			return !!this.campaign.letter_pdf
		},
		pdfDirectUrl() {
			// Return the PDF data URL for object/embed elements
			return this.campaign.letter_pdf
		},
		pdfSupported() {
			// Check if browser supports PDF display
			const userAgent = navigator.userAgent.toLowerCase()
			const isChrome = userAgent.indexOf('chrome') > -1
			const isFirefox = userAgent.indexOf('firefox') > -1
			const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1
			const isEdge = userAgent.indexOf('edge') > -1

			// Most modern browsers support PDF display
			return isChrome || isFirefox || isSafari || isEdge
		},
		selectedPattern() {
			if (this.selectedPatternIndex === null) return null
			return this.getCurrentPatterns()[this.selectedPatternIndex]
		},
		selectedTriggerUniqueUrlDomain() {
			return this.triggers.find(trigger => String(trigger.id) === String(this.campaign.trigger.type))?.unique_url_domain || ''
		},
		hasTemplateImage() {
			// Show whenever the template toggle is on for either side — patterns may be
			// empty (or not yet loaded if the base image is broken).
			return !!(this.campaign.front_is_template || this.campaign.back_is_template)
		},
		canTestTemplate() {
			return this.hasTemplateImage
		},
	},
	async mounted() {
		await this.loadTemplateFonts()
		// Initialize Bootstrap modals
		this.imageModal = new Modal(document.getElementById('imageInfoModal'))
		this.templateModal = new Modal(document.getElementById('templateSettingsModal'))
		this.htmlEditorModal = new Modal(document.getElementById('htmlEditorModal'))
		this.recipientSourceModal = new Modal(document.getElementById('recipientSourceModal'))

		// Template test modal: no backdrop, clean up the generated test when it closes.
		const templateTestEl = document.getElementById('templateTestModal')
		this.templateTestModalInstance = new Modal(templateTestEl, { backdrop: false })
		templateTestEl.addEventListener('hidden.bs.modal', this.cleanupTemplateTest)

		// Load triggers and senders from database
		await this.loadTriggers()
		await this.loadSenders()
		await this.loadEmailTemplates()
		await this.loadEmailProviderConfigs()

		// Check if we're editing an existing campaign
		if (this.$route.params.id) {
			this.isEditing = true
			this.campaignId = parseInt(this.$route.params.id)
			await this.loadCampaign()
		}
	},
	beforeUnmount() {
		// Clean up any ongoing drag operations
		if (this.dragState.isDragging) {
			document.removeEventListener('mousemove', this.onDragMove)
			document.removeEventListener('mouseup', this.onDragEnd)
			document.body.style.userSelect = ''
			document.body.style.cursor = ''
		}
		// Cleanup editor content
		this.editorContent = ''
		// Remove any leftover template test (LOB delivery + generated files)
		this.cleanupTemplateTest()
	},
	watch: {
		'campaign.type'(newType) {
			if (newType === 'postcard') {
				// Ensure postcard size is set to default if somehow empty
				if (!this.campaign.postcard_size) {
					this.campaign.postcard_size = '4x6'
				}
			}
		},
		'campaign.status'(newStatus) {
			// Clear pause duration when status changes away from paused
			if (newStatus !== 'paused') {
				this.campaign.pause_duration = ''
				this.campaign.pause_until = null
			}
		},
		pricePerItem: {
			handler(newPrice) {
				// Update the campaign item_price for backend
				this.campaign.item_price = parseFloat(newPrice)
			},
			immediate: true,
		},
		selectedPattern: {
			handler(newPattern) {
				if (newPattern) {
					// Recalculate dimensions when pattern properties change
					this.calculatePatternDimensions(newPattern)
				}
			},
			deep: true,
		},
	},
	methods: {
		// Save only the short domain, then move prepared run items' links to it.
		async applyDomainToUnsent() {
			this.applyingDomain = true
			try {
				await restApi.put(`/auto-campaigns/${this.campaignId}`, { shortlink_domain: this.campaign.shortlink_domain || null })
				const { data } = await restApi.post(`/auto-campaigns/${this.campaignId}/shortlinks/apply-domain`)
				this.$swal.fire({ icon: 'success', title: `${data.updated} run item link${data.updated === 1 ? '' : 's'} moved to the selected domain`, timer: 3000, showConfirmButton: false })
			} catch (error) {
				const errors = error.response?.data?.errors
				this.$swal.fire({ icon: 'error', title: (errors && Object.values(errors).flat()[0]) || error.response?.data?.message || 'Could not apply the domain.', timer: 6000 })
			} finally {
				this.applyingDomain = false
			}
		},
		async loadTemplateFonts() {
			this.templateFonts = await loadTemplateFonts()
		},
		getPreviewFontFamily(fontFamily) {
			return previewFontFamily(fontFamily)
		},
		getPreviewTextStroke(pattern) {
			return patternTextStroke(pattern, this.getDisplayFontSize(pattern.fontSize))
		},
		async loadTriggers() {
			try {
				const response = await restApi.get('/auto-campaign-triggers')
				this.triggers = response.data
			} catch (error) {
				console.error('Error loading triggers:', error)
				// Fallback to hardcoded triggers if API fails
				this.triggers = [
					{ id: 'clerkminutes_trial_started', name: 'ClerkMinutes Trial Started' },
					{ id: 'townweb_new_client', name: 'TownWeb New Client' },
					{ id: 'heygov_new_client', name: 'HeyGov New Client' },
					{ id: 'clerkminutes_new_client', name: 'ClerkMinutes New Client' },
				]
			}
		},
		async loadSenders() {
			try {
				const response = await restApi.get('/settings', {
					params: { name: 'LobSettings', type: 'settings' },
				})
				if (response.data && response.data.data) {
					const lobSettings = JSON.parse(response.data.data)
					this.senders = lobSettings.senders || []
				} else {
					this.senders = []
				}
			} catch (error) {
				console.error('Error loading senders:', error)
				// Fallback to empty senders array if API fails
				this.senders = []
			}
		},
		async loadEmailTemplates() {
			try {
				const response = await restApi.get('/email-templates')
				this.emailTemplates = (response.data || []).filter(template => template.status !== 'archived')
			} catch (error) {
				console.error('Error loading email templates:', error)
				this.emailTemplates = []
			}
		},
		async loadEmailProviderConfigs() {
			try {
				const response = await restApi.get('/email-provider-configs')
				this.emailProviderConfigs = (response.data || []).filter(config => config.status === 'active')
			} catch (error) {
				console.error('Error loading email provider configs:', error)
				this.emailProviderConfigs = []
			}
		},
		openRecipientSourceModal() {
			if (this.recipientSourceModal) {
				this.recipientSourceModal.show()
			}
		},
		setRecipientSource(value) {
			this.campaign.email_config.recipient_source = value
			if (this.recipientSourceModal) {
				this.recipientSourceModal.hide()
			}
		},
		async loadCampaign() {
			try {
				const response = await restApi.get(`/auto-campaigns/${this.campaignId}`)
				const campaign = response.data

				// Map API response to frontend structure (avoiding Object.assign due to structure mismatch)
				this.campaign.id = campaign.id || null
				this.campaign.name = campaign.name || ''
				this.campaign.description = campaign.description || ''
				this.campaign.utm_campaign = campaign.utm_campaign || ''
				this.campaign.utm_content = campaign.utm_content || ''
				this.campaign.type = campaign.type || ''
				this.campaign.mode = campaign.mode || ''
				this.campaign.status = campaign.status || 'draft'
				this.campaign.mail_type = campaign.mail_type || ''
				this.campaign.sender = campaign.sender || ''

				// Letter fields
				this.campaign.letter_color = campaign.letter_color ? campaign.letter_color.toString() : ''
				this.campaign.letter_double_sided = campaign.letter_double_sided ? campaign.letter_double_sided.toString() : ''
				this.campaign.letter_extra_service = campaign.letter_extra_service || ''
				this.campaign.letter_address_placement = campaign.letter_address_placement || ''
				this.campaign.letter_pages_count = campaign.letter_pages_count || 1
				this.campaign.letter_content_type = campaign.letter_pdf ? 'pdf' : 'html'
				this.campaign.letter_html = campaign.letter_pattern_html || ''

				// Postcard fields
				this.campaign.postcard_size = campaign.postcard_size || '4x6'

				// Trigger settings - handle the API structure properly
				this.campaign.trigger.type = campaign.trigger_id || ''
				this.campaign.unique_url_enabled = !!campaign.unique_url_enabled
				this.campaign.unique_url_domain = campaign.unique_url_domain || ''
				this.campaign.shortlink_slack_notify = !!campaign.shortlink_slack_notify
				this.campaign.shortlink_domain = campaign.shortlink_domain || null
				this.campaign.trigger.max_per_day = 100
				const emailConfig = campaign.email_config || {}
				this.campaign.email_config = {
					enabled: !!emailConfig.enabled,
					email_template_id: emailConfig.email_template_id || null,
					email_provider_config_id: emailConfig.email_provider_config_id || null,
					recipient_source: emailConfig.recipient_source || 'municipality_email_only',
					schedule_type: emailConfig.schedule_type || 'immediate',
					delay_value: emailConfig.delay_value || 0,
					delay_unit: emailConfig.delay_unit || 'hours',
					depends_on_channel: emailConfig.depends_on_channel || null,
					depends_on_event: emailConfig.depends_on_event || null,
				}

				// Load existing images if they exist
				if (campaign.postcard_front) {
					const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
					this.campaign.postcard_front = `${baseUrl}/storage/${campaign.postcard_front}`
				}

				if (campaign.postcard_back) {
					const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
					this.campaign.postcard_back = `${baseUrl}/storage/${campaign.postcard_back}`
				}

				// Load template patterns from postcard_pattern_type JSON
				if (campaign.postcard_pattern_type) {
					try {
						const allTemplates = JSON.parse(campaign.postcard_pattern_type)

						// Initialize pattern arrays
						this.campaign.front_patterns = []
						this.campaign.back_patterns = []

						// Separate patterns by image side
						allTemplates.forEach(pattern => {
							const patternData = {
								text: pattern.text,
								x: pattern.position.x,
								y: pattern.position.y,
								fontSize: pattern.fontSize || 18,
								fontWeight: pattern.fontWeight || '400',
								qrSize: pattern.qrSize || (pattern.text === '{unique_url_qr}' ? 220 : null),
								coordinateVersion: pattern.coordinateVersion || 1,
								color: pattern.color || '#000000',
								fontFamily: pattern.fontFamily || "PlusJakartaSans",
								patternHeight: pattern.patternHeight || 0,
								patternWidth: pattern.patternWidth || 0,
							}

							// Calculate dimensions for loaded patterns if not already set
							if (!patternData.patternHeight || !patternData.patternWidth) {
								this.calculatePatternDimensions(patternData)
							}

							if (pattern.image === 'front') {
								this.campaign.front_patterns.push(patternData)
								this.campaign.front_is_template = true
							} else if (pattern.image === 'back') {
								this.campaign.back_patterns.push(patternData)
								this.campaign.back_is_template = true
							}
						})
					} catch (error) {
						console.error('Error parsing postcard_pattern_type JSON:', error)
						// Initialize empty arrays on parse error
						this.campaign.front_patterns = []
						this.campaign.back_patterns = []
					}
				} else {
					// Initialize empty arrays if no patterns exist
					this.campaign.front_patterns = []
					this.campaign.back_patterns = []
				}

				if (campaign.letter_pdf) {
					const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
					this.campaign.letter_pdf = `${baseUrl}/storage/${campaign.letter_pdf}`
					this.campaign.letter_pdf_name = campaign.letter_pdf.split('/').pop()
				}
			} catch (error) {
				console.error('Error loading campaign:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to load campaign data',
					icon: 'error',
				})
				// Redirect back to list on error
				this.$router.push({ name: 'autoCampaigns' })
			}
		},
		getTriggerDisplayName(triggerType) {
			const trigger = this.triggers.find(t => t.id === triggerType)
			return trigger ? trigger.name : 'Not selected'
		},
		// HTML Editor methods
		getContentLength() {
			if (!this.campaign.letter_html) return 0
			// Remove HTML tags to get text length
			const textOnly = this.campaign.letter_html.replace(/<[^>]*>/g, '')
			return textOnly.length
		},
		openHtmlEditor() {
			this.htmlEditorModal.show()
		},
		removeHtmlContent() {
			this.campaign.letter_html = ''
		},
		insertVariable(variable) {
			// Insert variable into TinyMCE at cursor position
			if (this.$refs.tinymceEditor && this.$refs.tinymceEditor.editor) {
				const editor = this.$refs.tinymceEditor.editor
				editor.focus()
				editor.insertContent(variable)
			}
		},
		getTextLength() {
			if (!this.campaign.letter_html) return 0
			const textOnly = this.campaign.letter_html.replace(/<[^>]*>/g, '')
			return textOnly.length
		},
		getWordCount() {
			if (!this.campaign.letter_html) return 0
			const textOnly = this.campaign.letter_html.replace(/<[^>]*>/g, '')
			const words = textOnly
				.trim()
				.split(/\s+/)
				.filter(word => word.length > 0)
			return words.length
		},
		saveHtmlContent() {
			// The v-model binding automatically updates campaign.letter_html
			this.htmlEditorModal.hide()
			this.$swal.fire({
				title: 'Letter Content Saved',
				text: 'Your letter content has been saved successfully.',
				icon: 'success',
				timer: 2000,
				showConfirmButton: false,
			})
		},

		// PDF upload methods
		triggerPDFUpload() {
			this.$refs.pdfInput.click()
		},
		handlePDFUpload(event) {
			const file = event.target.files[0]
			if (file) {
				this.pdfFile = file // Store the actual file for upload
				this.pdfLoading = true
				this.pdfViewMethod = 'iframe' // Reset view method
				const reader = new FileReader()
				reader.onload = e => {
					this.campaign.letter_pdf = e.target.result
					this.campaign.letter_pdf_name = file.name
					// Give a small delay to show loading state
					setTimeout(() => {
						this.pdfLoading = false
					}, 500)
				}
				reader.readAsDataURL(file)
			}
		},
		onPdfLoad() {
			this.pdfLoading = false
		},
		onPdfError() {
			// If iframe fails, try object/embed
			if (this.pdfViewMethod === 'iframe') {
				this.pdfViewMethod = 'object'
			} else if (this.pdfViewMethod === 'object') {
				// If object/embed also fails, show fallback
				this.pdfViewMethod = 'fallback'
			}
		},
		viewPDF() {
			if (this.campaign.letter_pdf) {
				window.open(this.campaign.letter_pdf, '_blank')
			}
		},
		removePDF() {
			this.campaign.letter_pdf = null
			this.campaign.letter_pdf_name = ''
			this.pdfFile = null
			this.$refs.pdfInput.value = ''
		},
		// Postcard front image methods
		triggerFrontUpload() {
			this.$refs.frontImageInput.click()
		},
		handleFrontImageUpload(event) {
			const file = event.target.files[0]
			if (file) {
				this.frontImageFile = file
				const reader = new FileReader()
				reader.onload = e => {
					if (this.campaign.type === 'postcard') {
						this.campaign.postcard_front = e.target.result
					} else {
						this.campaign.front_image_url = e.target.result
					}
				}
				reader.readAsDataURL(file)
			}
		},
		removeFrontImage() {
			if (this.campaign.type === 'postcard') {
				this.campaign.postcard_front = null
			} else {
				this.campaign.front_image_url = null
			}
			this.frontImageFile = null
			this.$refs.frontImageInput.value = ''
		},
		// Postcard back image methods
		triggerBackUpload() {
			this.$refs.backImageInput.click()
		},
		handleBackImageUpload(event) {
			const file = event.target.files[0]
			if (file) {
				this.backImageFile = file
				const reader = new FileReader()
				reader.onload = e => {
					if (this.campaign.type === 'postcard') {
						this.campaign.postcard_back = e.target.result
					} else {
						this.campaign.back_image_url = e.target.result
					}
				}
				reader.readAsDataURL(file)
			}
		},
		removeBackImage() {
			if (this.campaign.type === 'postcard') {
				this.campaign.postcard_back = null
			} else {
				this.campaign.back_image_url = null
			}
			this.backImageFile = null
			this.$refs.backImageInput.value = ''
		},
		// Image info modal methods
		showFrontImageInfo() {
			if (this.frontImageFile) {
				this.showImageInfo(this.frontImageFile, this.campaign.postcard_front, 'Front Image')
			}
		},
		showBackImageInfo() {
			if (this.backImageFile) {
				this.showImageInfo(this.backImageFile, this.campaign.postcard_back, 'Back Image')
			}
		},
		showImageInfo(file, src, imageName) {
			const img = new Image()
			img.onload = () => {
				this.selectedImageInfo = {
					src: src,
					name: file.name || imageName,
					size: this.formatFileSize(file.size),
					dimensions: `${img.width} × ${img.height} pixels`,
					type: file.type || 'Unknown',
				}
				this.imageModal.show()
			}
			img.src = src
		},
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
		},
		validateForm() {
			const errors = []

			// Basic validation - required fields only
			if (!this.campaign.name) {
				errors.push('Campaign name is required')
			}
			if (!this.campaign.type) {
				errors.push('Mail type is required')
			}
			if (!this.campaign.mode) {
				errors.push('Campaign mode is required')
			}
			if (!this.campaign.status) {
				errors.push('Campaign status is required')
			}
			if (!this.campaign.mail_type) {
				errors.push('Mail service is required')
			}
			if (!this.campaign.sender) {
				errors.push('Campaign sender is required')
			}
			if (!this.campaign.trigger.type) {
				errors.push('Trigger type is required')
			}

			// Status-specific validation
			if (this.campaign.status === 'paused' && !this.campaign.pause_duration) {
				errors.push('Pause duration is required when status is paused')
			}

			// Type-specific validation
			if (this.campaign.type === 'letter') {
				// Letter content type is required
				if (!this.campaign.letter_content_type) {
					errors.push('Letter content type is required (PDF or HTML)')
				}
				// Validate based on content type
				if (this.campaign.letter_content_type === 'html') {
					if (!this.campaign.letter_html || this.campaign.letter_html.trim() === '') {
						errors.push('Letter HTML content is required')
					}
				} else if (this.campaign.letter_content_type === 'pdf') {
					if (!this.campaign.letter_pdf) {
						errors.push('Letter PDF file is required')
					}
				}
				// All letter fields are required
				if (!this.campaign.letter_color) {
					errors.push('Letter color option is required')
				}
				if (!this.campaign.letter_double_sided) {
					errors.push('Double sided option is required')
				}
				if (!this.campaign.letter_address_placement) {
					errors.push('Address placement is required')
				}
				if (!this.campaign.letter_type) {
					errors.push('Letter type is required')
				}
				if (!this.campaign.letter_pages_count || this.campaign.letter_pages_count < 1) {
					errors.push('Letter pages count is required and must be at least 1')
				}
			} else if (this.campaign.type === 'postcard') {
				// Front and back images are required for postcard
				if (!this.campaign.postcard_front) {
					errors.push('Postcard front image is required')
				}
				if (!this.campaign.postcard_back) {
					errors.push('Postcard back image is required')
				}
				if (!this.campaign.postcard_size) {
					errors.push('Postcard size is required')
				}
			}

			// Additional validation for file sizes and formats
			if (this.campaign.type === 'postcard') {
				// Check file sizes (max 10MB per image)
				if (this.frontImageFile && this.frontImageFile.size > 10 * 1024 * 1024) {
					errors.push('Front image file size must be less than 10MB')
				}
				if (this.backImageFile && this.backImageFile.size > 10 * 1024 * 1024) {
					errors.push('Back image file size must be less than 10MB')
				}

				// Check image formats
				const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
				if (this.frontImageFile && !validImageTypes.includes(this.frontImageFile.type)) {
					errors.push('Front image must be JPEG, PNG, or GIF format')
				}
				if (this.backImageFile && !validImageTypes.includes(this.backImageFile.type)) {
					errors.push('Back image must be JPEG, PNG, or GIF format')
				}
			}

			if (this.campaign.type === 'letter' && this.campaign.letter_content_type === 'pdf') {
				// Check PDF file size (max 20MB)
				if (this.pdfFile && this.pdfFile.size > 20 * 1024 * 1024) {
					errors.push('PDF file size must be less than 20MB')
				}

				// Check PDF format
				if (this.pdfFile && this.pdfFile.type !== 'application/pdf') {
					errors.push('Letter file must be PDF format')
				}
			}

			// Validate numerical values
			if (this.campaign.item_price && (isNaN(this.campaign.item_price) || this.campaign.item_price < 0)) {
				errors.push('Price per item must be a valid positive number')
			}

			if (this.campaign.trigger.max_per_day && (isNaN(this.campaign.trigger.max_per_day) || this.campaign.trigger.max_per_day < 1)) {
				errors.push('Max per day must be a positive number')
			}
			if (this.campaign.unique_url_enabled && !this.campaign.unique_url_domain?.trim() && !this.selectedTriggerUniqueUrlDomain) {
				errors.push('Unique URL tracking requires a destination URL or a fallback destination on the selected trigger')
			}

			return errors
		},
		calculatePauseUntil() {
			if (this.campaign.pause_duration === 'indefinite' || !this.campaign.pause_duration) {
				this.campaign.pause_until = null
				return
			}

			const now = new Date()
			let pauseUntil = new Date(now)

			switch (this.campaign.pause_duration) {
				case '7_days':
					pauseUntil.setDate(now.getDate() + 7)
					break
				case '2_weeks':
					pauseUntil.setDate(now.getDate() + 14)
					break
				case '1_month':
					pauseUntil.setMonth(now.getMonth() + 1)
					break
				case '3_months':
					pauseUntil.setMonth(now.getMonth() + 3)
					break
				case '6_months':
					pauseUntil.setMonth(now.getMonth() + 6)
					break
				case '1_year':
					pauseUntil.setFullYear(now.getFullYear() + 1)
					break
			}

			this.campaign.pause_until = pauseUntil.toISOString()
		},
		// Build the postcard_pattern_type payload (shared by save + template test) so the
		// preview renders exactly what gets persisted.
		buildTemplatePatterns() {
			const allTemplates = []

			if (this.campaign.front_is_template && this.campaign.front_patterns.length > 0) {
				this.campaign.front_patterns.forEach((pattern, index) => {
					allTemplates.push({
						image: 'front',
						index: index,
						text: pattern.text,
						position: { x: pattern.x, y: pattern.y },
						fontSize: pattern.fontSize,
						fontWeight: pattern.fontWeight,
						qrSize: pattern.qrSize,
						coordinateVersion: pattern.coordinateVersion || 1,
						color: pattern.color,
						fontFamily: pattern.fontFamily,
						patternHeight: pattern.patternHeight,
						patternWidth: pattern.patternWidth,
					})
				})
			}

			if (this.campaign.back_is_template && this.campaign.back_patterns.length > 0) {
				this.campaign.back_patterns.forEach((pattern, index) => {
					allTemplates.push({
						image: 'back',
						index: index,
						text: pattern.text,
						position: { x: pattern.x, y: pattern.y },
						fontSize: pattern.fontSize,
						fontWeight: pattern.fontWeight,
						qrSize: pattern.qrSize,
						coordinateVersion: pattern.coordinateVersion || 1,
						color: pattern.color,
						fontFamily: pattern.fontFamily,
						patternHeight: pattern.patternHeight,
						patternWidth: pattern.patternWidth,
					})
				})
			}

			return allTemplates
		},
		async openLocalTemplateTestModal() {
			const requestId = ++this.templateTestRequestId
			this.templateTest = { mode: 'local', loading: true, error: null, id: null, url: null, thumbnails: [], files: [] }
			this.templateTestModalInstance.show()

			try {
				const response = await restApi.post('/auto-campaign-lob/generate-template-preview', {
					campaign_id: this.campaignId,
					patterns: this.buildTemplatePatterns(),
				})
				const data = response.data.data
				const apiBase = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
				if (requestId !== this.templateTestRequestId) {
					await restApi.post('/auto-campaign-lob/delete-template-test', { files: data.files || [] })
					return
				}
				const thumbnails = (data.files || []).map(file => `${apiBase}/storage${file}?preview=${Date.now()}`)

				this.templateTest = {
					mode: 'local',
					loading: false,
					error: null,
					id: null,
					url: null,
					thumbnails,
					files: data.files || [],
				}
			} catch (error) {
				this.templateTest.loading = false
				this.templateTest.error = error.response?.data?.error || error.response?.data?.message || 'Failed to generate the local template preview.'
			}
		},
		async openLobTemplateTestModal() {
			const requestId = ++this.templateTestRequestId
			this.templateTest = { mode: 'lob', loading: true, error: null, id: null, url: null, thumbnails: [], files: [] }
			this.templateTestModalInstance.show()

			try {
				const response = await restApi.post('/auto-campaign-lob/send-template-test', {
					campaign_id: this.campaignId,
					patterns: this.buildTemplatePatterns(),
				})
				await new Promise(resolve => setTimeout(resolve, 15000))
				const data = response.data.data
				if (requestId !== this.templateTestRequestId) {
					await restApi.post('/auto-campaign-lob/delete-template-test', { id: data.id, files: data.files || [] })
					return
				}
				const thumbnails =
					data.thumbnails && data.thumbnails.length
						? data.thumbnails.map(thumb => thumb.large || thumb.medium || thumb.small).filter(Boolean)
						: data.thumbnail
						? [data.thumbnail]
						: []

				this.templateTest = { mode: 'lob', loading: false, error: null, id: data.id, url: data.url, thumbnails, files: data.files || [] }
			} catch (error) {
				this.templateTest.loading = false
				this.templateTest.error = error.response?.data?.message || 'Failed to generate the LOB template test.'
			}
		},
		closeTemplateTestModal() {
			// Hiding triggers `hidden.bs.modal` → cleanupTemplateTest().
			this.templateTestModalInstance.hide()
		},
		async cleanupTemplateTest() {
			this.templateTestRequestId += 1
			const id = this.templateTest.id
			const files = this.templateTest.files
			// Reset first so cleanup is idempotent and reopening starts fresh.
			this.templateTest = { mode: 'local', loading: false, error: null, id: null, url: null, thumbnails: [], files: [] }

			if (!id && (!files || !files.length)) {
				return
			}

			try {
				await restApi.post('/auto-campaign-lob/delete-template-test', { id, files })
			} catch (error) {
				// Best-effort cleanup; LOB also auto-expires test deliveries.
			}
		},
		async saveCampaign() {
			// Validate form before submitting
			const validationErrors = this.validateForm()
			if (validationErrors.length > 0) {
				this.$swal.fire({
					title: 'Validation Error',
					html: validationErrors.map(error => `• ${error}`).join('<br>'),
					icon: 'error',
					confirmButtonText: 'OK',
				})
				return
			}

			this.saving = true

			try {
				// Prepare allTemplates attribute with original font sizes (images are full-size now)
				const allTemplates = this.buildTemplatePatterns()

				// Prepare the campaign data for API
				const campaignData = {
					name: this.campaign.name,
					type: this.campaign.type,
					mode: this.campaign.mode,
					status: this.campaign.status,
					mail_type: this.campaign.mail_type,
					item_price: parseFloat(this.campaign.item_price),
					sender: this.campaign.sender,
					trigger_id: this.campaign.trigger.type,
					unique_url_enabled: this.campaign.unique_url_enabled,
					unique_url_domain: this.campaign.unique_url_domain?.trim() || null,
					shortlink_slack_notify: !!this.campaign.shortlink_slack_notify,
					shortlink_domain: this.campaign.shortlink_domain || null,
					utm_campaign: normalizeUtmCampaign(this.campaign.utm_campaign),
					utm_content: normalizeUtmCampaign(this.campaign.utm_content),
					postcard_pattern_type: JSON.stringify(allTemplates),
					email_config: {
						enabled: this.campaign.email_config.enabled,
						email_template_id: this.campaign.email_config.email_template_id,
						email_provider_config_id: this.campaign.email_config.email_provider_config_id,
						recipient_source: this.campaign.email_config.recipient_source,
						schedule_type: this.campaign.email_config.schedule_type,
						delay_value: this.campaign.email_config.delay_value,
						delay_unit: this.campaign.email_config.delay_unit,
						depends_on_channel:
							this.campaign.email_config.schedule_type === 'after_postcard_sent'
								? 'postcard'
								: this.campaign.email_config.schedule_type === 'after_letter_sent'
								? 'letter'
								: null,
						depends_on_event:
							this.campaign.email_config.schedule_type === 'after_postcard_sent' || this.campaign.email_config.schedule_type === 'after_letter_sent'
								? 'sent'
								: null,
					},
				}

				// Add optional fields only if they have values
				if (this.campaign.description) {
					campaignData.description = this.campaign.description
				}
				if (this.campaign.pause_duration) {
					campaignData.pause_duration = this.campaign.pause_duration
				}
				if (this.campaign.pause_until) {
					campaignData.pause_until = this.campaign.pause_until
				}

				// Add type-specific fields
				if (this.campaign.type === 'letter') {
					campaignData.letter_color = parseInt(this.campaign.letter_color)
					campaignData.letter_double_sided = parseInt(this.campaign.letter_double_sided)
					campaignData.letter_extra_service = this.campaign.letter_extra_service || null
					campaignData.letter_address_placement = this.campaign.letter_address_placement
					campaignData.letter_type = this.campaign.letter_type
					campaignData.letter_pages_count = parseInt(this.campaign.letter_pages_count)
					campaignData.letter_content_type = this.campaign.letter_content_type

					// Add content based on type
					if (this.campaign.letter_content_type === 'html') {
						campaignData.letter_html = this.campaign.letter_html
					}
				} else if (this.campaign.type === 'postcard') {
					campaignData.postcard_size = this.campaign.postcard_size
					campaignData.front_is_template = this.campaign.front_is_template
					campaignData.back_is_template = this.campaign.back_is_template
				}

				let response
				let campaignId

				if (this.isEditing) {
					// Update existing campaign
					response = await restApi.put(`/auto-campaigns/${this.campaignId}`, campaignData)
					campaignId = this.campaignId
				} else {
					// Create new campaign
					response = await restApi.post('/auto-campaigns', campaignData)
					campaignId = response.data.id
				}

				// Upload files if they exist

				// Upload postcard images if it's a postcard campaign
				if (this.campaign.type === 'postcard') {
					if (this.frontImageFile) {
						await this.uploadPostcardFront(campaignId, this.frontImageFile)
					}
					if (this.backImageFile) {
						await this.uploadPostcardBack(campaignId, this.backImageFile)
					}
				}

				// Upload PDF if it's a letter campaign with PDF content
				if (this.campaign.type === 'letter' && this.campaign.letter_content_type === 'pdf' && this.pdfFile) {
					await this.uploadLetterPdf(campaignId, this.pdfFile)
				}

				// Show success message
				await this.$swal.fire({
					title: 'Success!',
					text: `Auto campaign has been ${this.isEditing ? 'updated' : 'created'} successfully.`,
					icon: 'success',
					confirmButtonText: 'OK',
				})

				// Redirect to campaigns list
				this.$router.push({ name: 'autoCampaigns' })
			} catch (error) {
				console.error(`Error ${this.isEditing ? 'updating' : 'creating'} campaign:`, error)

				// Handle API validation errors
				if (error.response && error.response.status === 422) {
					const errors = error.response.data.errors
					const errorMessages = Object.values(errors).flat()

					this.$swal.fire({
						title: 'Validation Error',
						html: errorMessages.map(error => `• ${error}`).join('<br>'),
						icon: 'error',
						confirmButtonText: 'OK',
					})
				} else {
					this.$swal.fire({
						title: 'Error',
						text: `Failed to ${this.isEditing ? 'update' : 'create'} auto campaign. Please try again.`,
						icon: 'error',
						confirmButtonText: 'OK',
					})
				}
			} finally {
				this.saving = false
			}
		},

		// File upload methods
		async uploadPostcardFront(campaignId, file) {
			const formData = new FormData()
			formData.append('postcard_front', file)

			try {
				await restApi.post(`/auto-campaigns/${campaignId}/upload-postcard-front`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
			} catch (error) {
				console.error('Error uploading postcard front:', error)
				throw error
			}
		},

		async uploadPostcardBack(campaignId, file) {
			const formData = new FormData()
			formData.append('postcard_back', file)

			try {
				await restApi.post(`/auto-campaigns/${campaignId}/upload-postcard-back`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
			} catch (error) {
				console.error('Error uploading postcard back:', error)
				throw error
			}
		},

		async uploadLetterPdf(campaignId, file) {
			const formData = new FormData()
			formData.append('letter_pdf', file)

			try {
				await restApi.post(`/auto-campaigns/${campaignId}/upload-letter-pdf`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
			} catch (error) {
				console.error('Error uploading letter PDF:', error)
				throw error
			}
		},

		// Template methods
		openTemplateSettings(side) {
			this.currentTemplateSide = side
			this.selectedPatternIndex = null
			this.newPatternText = ''
			this.templateModal.show()

			// Capture image dimensions once the image is actually loaded AND laid out.
			// The image may be uncached (still loading after the modal opens) or the
			// modal may still be animating (clientWidth === 0), so we poll until both
			// are true instead of guessing a fixed delay. A fixed 100ms delay was a
			// race: when it expired before the image finished loading, every
			// pixel<->percentage conversion fell back to hard-coded dimensions and the
			// variable got saved/stamped in the wrong position and size.
			this.$nextTick(() => {
				this.ensureImageDimensions(side)
			})
		},
		onTemplateImageLoad() {
			// Fires whenever the <img> finishes loading — including when the user
			// switches sides and the src changes. Guarantees dimensions are captured
			// even if the image was not ready when the modal opened.
			if (this.currentTemplateSide) {
				this.captureImageDimensions(this.currentTemplateSide)
			}
		},
		ensureImageDimensions(side, attempt = 0) {
			// Retry until the image is loaded and measurable. ~2s ceiling (40 x 50ms)
			// so a genuinely failing image can't loop forever; the fallback in
			// pixelsToPercentage/getScaledFontSize then acts as a last resort.
			const maxAttempts = 40
			if (this.captureImageDimensions(side)) {
				return
			}
			if (attempt >= maxAttempts) {
				console.warn(`Template image for "${side}" not ready after ${maxAttempts} attempts; using fallback dimensions.`)
				return
			}
			setTimeout(() => this.ensureImageDimensions(side, attempt + 1), 50)
		},
		captureImageDimensions(side) {
			try {
				const templateImage = this.$refs.templateImage
				if (!templateImage) {
					return false
				}

				const actualWidth = templateImage.naturalWidth
				const actualHeight = templateImage.naturalHeight
				const displayWidth = templateImage.clientWidth
				const displayHeight = templateImage.clientHeight

				// Require a fully loaded image AND a laid-out element. Capturing zero
				// dimensions would poison every pixel<->percentage conversion, which is
				// exactly the bug we are fixing — so bail and let the caller retry.
				if (!templateImage.complete || !actualWidth || !actualHeight || !displayWidth || !displayHeight) {
					return false
				}

				this.imageDimensions[side] = {
					actual: { width: actualWidth, height: actualHeight },
					display: { width: displayWidth, height: displayHeight },
				}

				// Update pattern coordinates now that we have image dimensions
				this.updatePatternCoordinatesForSide(side)
				// Overlay font sizes are derived from the <img> directly (getDisplayFontSize),
				// so force a re-render to reflect the now-correct scaling in the preview.
				this.$forceUpdate()
				return true
			} catch (error) {
				console.error('Error capturing image dimensions:', error)
				return false
			}
		},
		updatePatternCoordinatesForSide(side) {
			return side
		},
		getCurrentTemplateImage() {
			return this.currentTemplateSide === 'front' ? this.campaign.postcard_front : this.campaign.postcard_back
		},
		getCurrentPatterns() {
			return this.currentTemplateSide === 'front' ? this.campaign.front_patterns : this.campaign.back_patterns
		},
		getPatternPreviewText(text) {
			const replacements = {
				'{name}': 'City of Sample',
				'{address}': '123 Main Street',
				'{city}': 'Anytown',
				'{state}': 'NY',
				'{zip}': '10001',
				'{country}': 'US',
				'{description}': '',
				// Printed text is the short form; the QR carries the full URL with UTM params.
				'{unique_url}': 'twn.link/a7k2m',
			}
			return Object.entries(replacements).reduce((value, [token, sample]) => value.split(token).join(sample), text || '')
		},
		handleImageClick(event) {
			if (!this.newPatternText.trim()) {
				this.$swal.fire({
					title: 'No Pattern Text',
					text: 'Please enter pattern text first before placing it on the image.',
					icon: 'warning',
				})
				return
			}

			const templateImage = this.$refs.templateImage
			if (!templateImage) return
			const rect = templateImage.getBoundingClientRect()
			const x = ((event.clientX - rect.left) / rect.width) * 100
			const y = ((event.clientY - rect.top) / rect.height) * 100

			this.addPatternAtPosition(x, y)
		},
		addPattern() {
			if (!this.newPatternText.trim()) {
				this.$swal.fire({
					title: 'Empty Pattern',
					text: 'Please enter pattern text.',
					icon: 'warning',
				})
				return
			}
			// Add pattern at center of image
			this.addPatternAtPosition(50, 50)
		},
		insertPatternVariable(token) {
			if (token === '{unique_url_qr}') {
				this.newPatternText = token
				return
			}
			const spacer = this.newPatternText && !this.newPatternText.endsWith(' ') ? ' ' : ''
			this.newPatternText += spacer + token
		},
		getDisplayFontSize(actualFontSize) {
			// Convert actual (scaled) font size to display size for UI
			try {
				const templateImage = this.$refs.templateImage
				if (!templateImage) {
					return actualFontSize
				}

				const actualWidth = templateImage.naturalWidth
				const displayedWidth = templateImage.clientWidth

				if (!actualWidth || !displayedWidth) {
					return actualFontSize
				}

				const scaleRatio = actualWidth / displayedWidth
				return Math.round(actualFontSize / scaleRatio)
			} catch (error) {
				console.error('Error calculating display font size:', error)
				return actualFontSize
			}
		},
		async getScaledFontSize(displayFontSize, imageSide = null) {
			// Use stored image dimensions for scaling, with fallback to direct image loading
			try {
				const side = imageSide || this.currentTemplateSide

				// Try to use stored dimensions first
				if (side && this.imageDimensions[side] && this.imageDimensions[side].actual && this.imageDimensions[side].display) {
					const { actual, display } = this.imageDimensions[side]

					if (actual.width && display.width) {
						const scaleRatio = actual.width / display.width
						const scaledFontSize = Math.round(displayFontSize * scaleRatio)

						return scaledFontSize
					}
				}

				// Fallback: Load image directly to get dimensions
				console.warn(`No stored dimensions for ${side}, attempting to load image directly`)
				const imageUrl = side === 'front' ? this.campaign.postcard_front : this.campaign.postcard_back

				if (imageUrl) {
					const scaleRatio = await this.getImageScaleRatio(imageUrl)
					if (scaleRatio > 1) {
						const scaledFontSize = Math.round(displayFontSize * scaleRatio)
						return scaledFontSize
					}
				}

				// Ultimate fallback: use a reasonable default scale for typical postcard images
				console.warn('Could not determine image scale, using default 2.5x scale for postcards')
				return Math.round(displayFontSize * 2.5)
			} catch (error) {
				console.error('Error calculating scaled font size:', error)
				// Use reasonable default scale as fallback
				return Math.round(displayFontSize * 2.5)
			}
		},
		async getImageScaleRatio(imageUrl) {
			return new Promise(resolve => {
				const img = new Image()
				img.onload = () => {
					// Assume modal displays images at max 450px width
					const modalMaxWidth = 450
					const actualWidth = img.naturalWidth
					const scaleRatio = actualWidth / modalMaxWidth
					resolve(scaleRatio)
				}
				img.onerror = () => {
					resolve(2.5) // Default fallback ratio
				}
				img.src = imageUrl
			})
		},
		addPatternAtPosition(x, y) {
			const newPattern = {
				text: this.newPatternText.trim(),
				x: Math.min(Math.max(x, 0), 95), // Ensure pattern stays within image bounds
				y: Math.min(Math.max(y, 0), 95),
				fontSize: 48, // Larger default size for full-size images
				color: '#000000',
				fontFamily: "PlusJakartaSans",
				fontWeight: '400',
				qrSize: this.newPatternText.trim() === '{unique_url_qr}' ? 220 : null,
				coordinateVersion: 2,
				patternHeight: 0, // Will be calculated
				patternWidth: 0, // Will be calculated
			}

			// Calculate actual dimensions and store in pattern
			this.calculatePatternDimensions(newPattern)

			if (this.currentTemplateSide === 'front') {
				this.campaign.front_patterns.push(newPattern)
			} else {
				this.campaign.back_patterns.push(newPattern)
			}

			// Select the newly added pattern
			this.selectedPatternIndex = this.getCurrentPatterns().length - 1
			this.newPatternText = ''
		},
		calculatePatternDimensions(pattern) {
			// Calculate actual pattern dimensions using Canvas API
			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')

			// Set font properties to match the pattern
			const fontWeight = pattern.fontWeight || '400'
			const fontSize = pattern.fontSize || 48
			const fontFamily = this.getPreviewFontFamily(pattern.fontFamily || "PlusJakartaSans")
			ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

			// Measure text dimensions
			const metrics = ctx.measureText(pattern.text)
			const actualWidth = metrics.width
			const actualHeight = fontSize * 1.2 // Approximate height including ascenders/descenders

			// Store actual dimensions in pattern
			pattern.patternWidth = actualWidth
			pattern.patternHeight = actualHeight

			return { width: actualWidth, height: actualHeight }
		},
		pixelsToPercentage(pixels, isWidth = true, imageSide = null) {
			// Convert pixels to percentage based on actual image dimensions
			const side = imageSide || this.currentTemplateSide || 'front'

			// Try to get actual image dimensions
			if (this.imageDimensions[side] && this.imageDimensions[side].actual) {
				const actualDimensions = this.imageDimensions[side].actual
				const referenceSize = isWidth ? actualDimensions.width : actualDimensions.height

				if (referenceSize && referenceSize > 0) {
					return (pixels / referenceSize) * 100
				}
			}

			// Fallback to standard postcard dimensions if actual dimensions not available
			const fallbackSize = isWidth ? 600 : 400
			console.log('fallbackSize', fallbackSize)
			return (pixels / fallbackSize) * 100
		},
		selectPattern(index) {
			this.selectedPatternIndex = index
		},
		removePattern(index) {
			if (this.currentTemplateSide === 'front') {
				this.campaign.front_patterns.splice(index, 1)
			} else {
				this.campaign.back_patterns.splice(index, 1)
			}

			// Reset selection if removed pattern was selected
			if (this.selectedPatternIndex === index) {
				this.selectedPatternIndex = null
			} else if (this.selectedPatternIndex > index) {
				this.selectedPatternIndex -= 1
			}
		},
		saveTemplateSettings() {
			// Console log all template patterns with their properties
			const allTemplates = []

			// Add front image templates if they exist
			if (this.campaign.front_is_template && this.campaign.front_patterns.length > 0) {
				this.campaign.front_patterns.forEach((pattern, index) => {
					// Convert patternWidth to percentage and subtract half from x
					allTemplates.push({
						image: 'front',
						index: index,
						text: pattern.text,
						position: { x: pattern.x, y: pattern.y },
						fontSize: pattern.fontSize,
						fontWeight: pattern.fontWeight,
						qrSize: pattern.qrSize,
						coordinateVersion: pattern.coordinateVersion || 1,
						color: pattern.color,
						fontFamily: pattern.fontFamily,
						patternHeight: pattern.patternHeight,
						patternWidth: pattern.patternWidth,
					})
				})
			}

			// Add back image templates if they exist
			if (this.campaign.back_is_template && this.campaign.back_patterns.length > 0) {
				this.campaign.back_patterns.forEach((pattern, index) => {
					// Convert patternWidth to percentage and subtract half from x
					allTemplates.push({
						image: 'back',
						index: index,
						text: pattern.text,
						position: { x: pattern.x, y: pattern.y },
						fontSize: pattern.fontSize,
						fontWeight: pattern.fontWeight,
						qrSize: pattern.qrSize,
						coordinateVersion: pattern.coordinateVersion || 1,
						color: pattern.color,
						fontFamily: pattern.fontFamily,
						patternHeight: pattern.patternHeight,
						patternWidth: pattern.patternWidth,
					})
				})
			}

			this.templateModal.hide()
			this.$swal.fire({
				title: 'Template Settings Saved',
				text: `Pattern settings for ${this.currentTemplateSide} image have been saved.`,
				icon: 'success',
				timer: 2000,
				showConfirmButton: false,
			})
		},
		// Drag functionality methods
		showDragHandle(index) {
			this.hoveredPatternIndex = index
		},
		hideDragHandle() {
			if (!this.dragState.isDragging) {
				this.hoveredPatternIndex = null
			}
		},
		startDrag(event, index) {
			event.preventDefault()
			event.stopPropagation()

			// Select the pattern being dragged
			this.selectedPatternIndex = index

			const currentPattern = this.getCurrentPatterns()[index]

			this.dragState = {
				isDragging: true,
				patternIndex: index,
				startX: event.clientX,
				startY: event.clientY,
				initialPatternX: currentPattern.x,
				initialPatternY: currentPattern.y,
			}

			// Add global mouse event listeners
			document.addEventListener('mousemove', this.onDragMove)
			document.addEventListener('mouseup', this.onDragEnd)

			// Prevent text selection and image dragging during drag
			document.body.style.userSelect = 'none'
			document.body.style.cursor = 'grabbing'
		},
		onDragMove(event) {
			if (!this.dragState.isDragging) return

			const templateImage = this.$refs.templateImage
			if (!templateImage) return
			const rect = templateImage.getBoundingClientRect()

			// Calculate the movement delta
			const deltaX = event.clientX - this.dragState.startX
			const deltaY = event.clientY - this.dragState.startY

			// Convert pixel delta to percentage delta
			const deltaXPercent = (deltaX / rect.width) * 100
			const deltaYPercent = (deltaY / rect.height) * 100

			// Calculate new position
			let newX = this.dragState.initialPatternX + deltaXPercent
			let newY = this.dragState.initialPatternY + deltaYPercent

			// Clamp to image boundaries (with some margin for text visibility)
			newX = Math.min(Math.max(newX, 2), 95)
			newY = Math.min(Math.max(newY, 2), 95)

			// Update pattern position
			const patterns = this.getCurrentPatterns()
			if (patterns[this.dragState.patternIndex]) {
				patterns[this.dragState.patternIndex].x = newX
				patterns[this.dragState.patternIndex].y = newY
			}
		},
		onDragEnd() {
			if (!this.dragState.isDragging) return

			// Clean up drag state
			this.dragState = {
				isDragging: false,
				patternIndex: null,
				startX: 0,
				startY: 0,
				initialPatternX: 0,
				initialPatternY: 0,
			}

			// Remove global event listeners
			document.removeEventListener('mousemove', this.onDragMove)
			document.removeEventListener('mouseup', this.onDragEnd)

			// Restore normal cursor and text selection
			document.body.style.userSelect = ''
			document.body.style.cursor = ''

			// Hide drag handle if not hovering
			this.hoveredPatternIndex = null
		},
	},
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.create-campaign-container {
	display: flex;
	flex-direction: column;
	gap: 40px;
	padding: 0;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.page-title {
	margin: 0 0 4px 0;
	line-height: 1.2;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
	line-height: 1.4;
}

/* Left Column - Preview Container */
.preview-container {
	position: sticky;
	top: 24px;
}

.preview-title {
	font-size: 18px;
	font-weight: 600;
	color: #1e293b;
	margin-bottom: 16px;
}

.image-title {
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 8px;
}

/* PDF Preview Section */
.pdf-preview-section {
	margin-bottom: 24px;
}

.pdf-preview-wrapper {
	border: 2px dashed #e2e8f0;
	border-radius: 12px;
	background: #f8fafc;
	padding: 24px;
	text-align: center;
}

.pdf-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
}

.pdf-preview-image {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
}

.pdf-icon {
	color: #dc2626;
}

.pdf-name {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	margin: 0;
	word-break: break-all;
}

.pdf-actions {
	display: flex;
	gap: 8px;
}

.pdf-upload-placeholder {
	cursor: pointer;
	transition: all 0.2s ease;
	padding: 32px 16px;
}

.pdf-upload-placeholder:hover {
	background: #f1f5f9;
	border-color: #cbd5e1;
}

.upload-icon {
	color: #94a3b8;
	margin-bottom: 12px;
}

.upload-text {
	font-size: 16px;
	font-weight: 500;
	color: #64748b;
	margin: 0 0 8px 0;
}

.upload-hint {
	font-size: 12px;
	color: #94a3b8;
}

/* Postcard Preview Section */
.postcard-preview-section {
	margin-bottom: 24px;
}

.postcard-image-wrapper {
	margin-bottom: 20px;
}

.postcard-image-container {
	width: 100%;
	min-height: 200px;
	border-radius: 12px;
	overflow: hidden;
	background: #f8fafc;
	border: 2px dashed #e2e8f0;
	position: relative;
	transition: height 0.3s ease;
}

.postcard-image {
	width: 100%;
	height: 100%;
	position: relative;
}

.postcard-image img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.image-overlay {
	position: absolute;
	top: 12px;
	right: 12px;
	display: flex;
	gap: 8px;
}

.overlay-icon {
	width: 32px;
	height: 32px;
	background: rgba(0, 0, 0, 0.7);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	cursor: pointer;
	transition: all 0.2s ease;
}

.overlay-icon:hover {
	transform: scale(1.1);
}

.overlay-icon.info-icon {
	background: rgba(147, 51, 234, 0.8);
	color: white;
}

.overlay-icon.info-icon:hover {
	background: rgba(147, 51, 234, 1);
}

.overlay-icon.delete-icon {
	background: rgba(239, 68, 68, 0.8);
	color: white;
}

.overlay-icon.delete-icon:hover {
	background: rgba(239, 68, 68, 1);
}

.overlay-icon.template-icon {
	background: rgba(16, 185, 129, 0.8);
	color: white;
}

.overlay-icon.template-icon:hover {
	background: rgba(16, 185, 129, 1);
}

.image-upload-placeholder {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
}

.image-upload-placeholder:hover {
	background: #f1f5f9;
	border-color: #cbd5e1;
}

.placeholder-icon {
	color: #94a3b8;
	margin-bottom: 12px;
}

.placeholder-text {
	color: #64748b;
	font-weight: 500;
	margin: 0;
}

.size-hint {
	color: #94a3b8;
	font-size: 12px;
	margin-top: 4px;
}

/* Type Placeholder */
.type-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	border: 2px dashed #e2e8f0;
	border-radius: 12px;
	background: #f8fafc;
}

.arrow-icon {
	color: #cbd5e1;
	margin-bottom: 16px;
}

.placeholder-message {
	color: #64748b;
	font-size: 16px;
	margin: 0;
}

/* Right Column - Form */
.form-column {
	border-radius: 14px;
	border: 1px solid var(--Borders-border-primary, #e0e1e1);
	padding: 24px;
}

.form-section {
	margin-bottom: 40px;
}

.section-title {
	font-size: 20px;
	font-weight: 600;
	color: #1e293b;
	margin: 0 0 8px 0;
}

.section-description {
	font-size: 14px;
	color: #64748b;
	margin: 0 0 24px 0;
	line-height: 1.5;
}

.form-group {
	margin-bottom: 20px;
}

.form-label {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	margin-bottom: 6px;
	display: block;
}

.required {
	color: #ef4444;
}

.form-control,
.form-select {
	display: flex;
	padding: 8px 12px;
	justify-content: center;
	align-items: center;
	gap: 8px;
	align-self: stretch;
	border-radius: 8px;
	border: 1px solid var(--Borders-border-primary, #e0e1e1);
	background: var(--Surfaces-surface, #fff);
	font-size: 14px;
	transition: all 0.2s ease;
	width: 100%;
}

.form-control:focus,
.form-select:focus {
	outline: none;
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Radio Button Styling */
.mail-type-selection,
.mode-selection,
.status-selection {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.mail-type-selection-inline,
.mode-selection-inline {
	display: flex;
	flex-direction: row;
	gap: 24px;
	flex-wrap: wrap;
}

.form-check {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 12px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	transition: all 0.2s ease;
}

.form-check:hover {
	background: #f8fafc;
	border-color: #d1d5db;
}

.form-check-input:checked + .form-check-label {
	color: #1e293b;
}

.form-check-input {
	margin: 0;
	margin-top: 2px;
}

.form-check-label {
	font-size: 14px;
	color: #64748b;
	cursor: pointer;
	margin: 0;
	flex: 1;
}

.form-check-inline {
	display: inline-flex;
	margin-right: 24px;
	flex: 1;
	min-width: 200px;
}

.pause-duration-group {
	margin-top: 16px;
	padding: 16px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	background: #f8fafc;
}

.pause-duration-group .form-label {
	color: #374151;
	font-weight: 600;
}

.pause-duration-group .form-text {
	margin-top: 8px;
	font-size: 13px;
	color: #10b981;
	font-weight: 500;
}

/* Status-specific styling */
.form-check.status-active {
	background: #f0fdf4;
	border-color: #22c55e;
}

.form-check.status-draft {
	background: #fefce8;
	border-color: #eab308;
}

.form-check.status-paused {
	background: #fef2f2;
	border-color: #ef4444;
}

.settings-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
}

/* Action Buttons */
.form-actions {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding-top: 24px;
	border-top: 1px solid #e5e7eb;
	margin-top: 32px;
}

.btn {
	display: flex;
	padding: 8px 16px;
	justify-content: center;
	align-items: center;
	gap: 8px;
	border-radius: 99px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	text-decoration: none;
}

.btn-sm {
	padding: 6px 12px;
	font-size: 12px;
}

.btn-outline-primary {
	background: transparent;
	border-color: #3b82f6;
	color: #3b82f6;
}

.btn-outline-primary:hover {
	background: #3b82f6;
	color: white;
	text-decoration: none;
}

.btn-outline-danger {
	background: transparent;
	border-color: #ef4444;
	color: #ef4444;
}

.btn-outline-danger:hover {
	background: #ef4444;
	color: white;
	text-decoration: none;
}

.btn-secondary {
	border: 1px solid var(--Components-buttons-secondary-border, #e0e1e1);
	background: var(--Components-buttons-secondary-bg, #fff);
	color: var(--Components-buttons-secondary-text, #374151);
}

.btn-secondary:hover {
	background: var(--Components-buttons-secondary-bg-hover, #f8f9fa);
	border-color: var(--Components-buttons-secondary-border-hover, #d1d5db);
	color: var(--Components-buttons-secondary-text-hover, #374151);
	text-decoration: none;
}

.btn-primary {
	border: 1px solid var(--Components-buttons-primary-border, #335cff);
	background: var(--Components-buttons-primary-bg, #335cff);
	color: white;
}

.btn-primary:hover {
	background: var(--Components-buttons-primary-bg-hover, #2847e6);
	border-color: var(--Components-buttons-primary-border-hover, #2847e6);
	color: white;
	text-decoration: none;
}

.btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
	.create-campaign-container {
		padding: 16px;
	}

	.page-title {
		font-size: 24px;
	}

	.settings-grid {
		grid-template-columns: 1fr;
	}

	.form-actions {
		flex-direction: column-reverse;
	}

	.btn {
		width: 100%;
		justify-content: center;
	}

	.postcard-image-container {
		min-height: 150px;
	}
}

/* Custom Modal Styling */
.modal-content {
	border-radius: 12px;
	border: none;
	box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
	padding: 20px 24px;
	border-bottom: 1px solid #e5e7eb;
}

.modal-title {
	font-size: 18px;
	font-weight: 600;
	color: #1f2937;
}

.btn-close {
	background: none;
	border: none;
	font-size: 16px;
	color: #6b7280;
	cursor: pointer;
	padding: 8px;
	border-radius: 4px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 1;
}

.btn-close:hover {
	background: #f3f4f6;
	color: #374151;
	opacity: 1;
}

.modal-body {
	padding: 24px;
}

.image-preview {
	text-align: center;
	margin-bottom: 20px;
}

.modal-image {
	max-width: 100%;
	max-height: 200px;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.image-details {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
	border-bottom: none;
}

.detail-label {
	font-weight: 500;
	color: #374151;
	font-size: 14px;
}

.detail-value {
	color: #6b7280;
	font-size: 14px;
	text-align: right;
}

.modal-footer {
	padding: 16px 24px;
	border-top: 1px solid #e5e7eb;
	justify-content: flex-end;
}

/* Form Actions and Pricing Styles */
.form-actions {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid var(--Borders-border-primary, #e0e1e1);
}

.pricing-display {
	flex: 1;
}

.pricing-info {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #374151;
	letter-spacing: -0.025em;
}

.price-value {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
	font-size: 26px;
	font-weight: 700;
	color: #10b981;
	letter-spacing: -0.03em;
}

.action-buttons {
	display: flex;
	gap: 12px;
	align-items: center;
}

@media (max-width: 768px) {
	.form-actions {
		flex-direction: column;
		gap: 16px;
		align-items: stretch;
	}

	.pricing-display {
		text-align: center;
		order: -1;
	}

	.action-buttons {
		justify-content: center;
	}
}

/* PDF Viewer Styles */
.pdf-viewer-container {
	position: relative;
	height: 660px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
	background: #f9fafb;
	width: 100%;
}

.pdf-viewer-wrapper {
	width: 100%;
	height: 100%;
	position: relative;
}

.pdf-viewer {
	width: 100%;
	height: 100%;
	border: none;
}

.pdf-fallback {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f9fafb;
	z-index: 5;
}

.fallback-content {
	text-align: center;
	padding: 20px;
}

.fallback-content .pdf-icon {
	color: #ef4444;
	margin-bottom: 16px;
}

.fallback-content h4 {
	font-size: 18px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 8px;
}

.fallback-content .pdf-name {
	font-size: 14px;
	color: #6b7280;
	margin-bottom: 8px;
	word-break: break-all;
}

.fallback-message {
	font-size: 13px;
	color: #9ca3af;
	margin-bottom: 16px;
	line-height: 1.4;
}

.pdf-loading {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: rgba(249, 250, 251, 0.9);
	z-index: 10;
}

.pdf-loading p {
	margin-top: 12px;
	font-size: 14px;
	color: #6b7280;
}

.pdf-info {
	padding: 8px 12px;
	background: #f3f4f6;
	border-radius: 6px;
	margin: 8px 0;
}

.pdf-name {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	margin: 0;
	word-break: break-all;
}

.pdf-actions {
	display: flex;
	gap: 8px;
	justify-content: center;
}

/* PDF Upload Placeholder Styles */
.pdf-upload-placeholder {
	background: #fafbfc;
	border: 2px dashed #d1d5db;
	border-radius: 12px;
	padding: 40px 20px;
	text-align: center;
	cursor: pointer;
	transition: all 0.3s ease;
}

.pdf-upload-placeholder:hover {
	border-color: #335cff;
	background: rgba(51, 92, 255, 0.02);
	transform: translateY(-1px);
}

.upload-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
}

.upload-icon-wrapper {
	width: 80px;
	height: 80px;
	background: linear-gradient(135deg, #335cff 0%, #4c6eff 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	margin-bottom: 8px;
}

.upload-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2937;
	margin: 0;
}

.upload-description {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.upload-formats {
	display: flex;
	gap: 8px;
	margin-top: 8px;
}

.format-badge {
	background: #e5e7eb;
	color: #374151;
	padding: 6px 16px;
	border-radius: 16px;
	font-size: 12px;
	font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
	.pdf-upload-placeholder {
		padding: 30px 16px;
	}

	.upload-icon-wrapper {
		width: 60px;
		height: 60px;
	}

	.upload-title {
		font-size: 18px;
	}

	.pdf-viewer-container {
		height: 450px;
	}
}

@media (max-width: 480px) {
	.pdf-upload-placeholder {
		padding: 24px 16px;
	}

	.upload-icon-wrapper {
		width: 50px;
		height: 50px;
	}

	.upload-title {
		font-size: 16px;
	}

	.pdf-viewer-container {
		height: 350px;
	}
}

/* Template Toggle Styles */
.template-toggle-container {
	display: flex;
	align-items: center;
}

.template-toggle-container .form-check-label {
	font-size: 12px;
	color: #6b7280;
	margin-left: 8px;
	font-weight: 500;
}

.template-toggle-container .form-check-input:checked {
	background-color: #10b981;
	border-color: #10b981;
}

/* Template Modal Styles */
.template-modal {
	max-width: 95vw !important;
	width: 95vw !important;
	height: 95vh !important;
	margin: 2.5vh auto !important;
}

.template-modal .modal-content {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.template-modal .modal-footer {
	flex-shrink: 0; /* Prevent footer from shrinking */
	display: flex !important;
	justify-content: flex-end;
	gap: 12px;
}

.template-modal-body {
	display: flex;
	flex: 1;
	padding: 0;
	overflow: hidden;
	min-height: 0; /* Allow flex item to shrink below content size */
}

.template-image-main-container {
	flex: 1;
	display: flex;
	flex-direction: column;
	background: #f8f9fa;
	border-right: 1px solid #e5e7eb;
	position: relative;
}

.template-image-scroll-container {
	flex: 1;
	overflow: auto;
	padding: 20px;
	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	max-width: calc(95vw - 420px); /* Account for sidebar width */
}

/* Custom scrollbar styling */
.template-image-scroll-container::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.template-image-scroll-container::-webkit-scrollbar-track {
	background: #f1f3f4;
	border-radius: 4px;
}

.template-image-scroll-container::-webkit-scrollbar-thumb {
	background: #c1c7cd;
	border-radius: 4px;
}

.template-image-scroll-container::-webkit-scrollbar-thumb:hover {
	background: #a8b0b8;
}

.template-image-container {
	position: relative;
	display: inline-block;
	cursor: crosshair;
	border: 2px solid #e5e7eb;
	border-radius: 8px;
	background: white;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.template-preview-image {
	display: block;
	width: auto;
	height: auto;
	/* Allow image to be at natural size but constrain container */
	min-width: 300px; /* Minimum size for usability */
}

.template-sidebar {
	width: 420px;
	flex-shrink: 0;
	background: white;
	padding: 20px;
	overflow-y: auto;
	border-left: 1px solid #e5e7eb;
	display: flex;
	flex-direction: column;
}

.sidebar-footer {
	background: #f8fafc;
	margin: 0 -20px -20px -20px;
	padding: 16px 20px 20px 20px;
	border-radius: 0 0 0 8px;
}

.text-pattern-overlay {
	position: absolute;
	cursor: pointer;
	user-select: none;
	padding: 0;
	background: transparent;
	border-radius: 4px;
	border: 2px solid transparent;
	transition: all 0.2s ease;
	white-space: nowrap;
	/* Coordinate v2 and QR use a top-left anchor; legacy text gets its baseline correction below. */
	transform: none;
	z-index: 10;
	display: flex;
	align-items: center;
	gap: 6px;
}

.placement-hint {
	padding: 10px 12px;
	margin: 10px 0;
	border-radius: 10px;
	background: #eef4ff;
	color: #3156a3;
	font-size: 12px;
	line-height: 1.5;
}

.variable-helper {
	display: grid;
	gap: 7px;
	padding: 12px;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	background: #f8fafc;
}

.variable-helper-title { font-size: 12px; font-weight: 700; color: #475569; }
.variable-chip {
	display: grid;
	grid-template-columns: 92px 1fr;
	gap: 8px;
	align-items: center;
	width: 100%;
	padding: 7px 9px;
	border: 1px solid transparent;
	border-radius: 8px;
	background: #fff;
	color: #64748b;
	font-size: 11px;
	text-align: left;
}
.variable-chip:hover { border-color: #3b82f6; background: #eff6ff; }
.variable-chip code { color: #315efb; font-weight: 700; }
.pattern-text { display: flex; flex-direction: column; min-width: 0; }
.pattern-text small { color: #94a3b8; font-size: 10px; margin-top: 2px; }

.pattern-remove-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin-top: 18px;
	padding: 11px 14px;
	border: 1px solid #fecaca;
	border-radius: 10px;
	background: #fff;
	color: #dc2626;
	font-size: 13px;
	font-weight: 700;
}

.pattern-remove-btn:hover {
	border-color: #ef4444;
	background: #fef2f2;
}

.text-pattern-overlay:hover {
	background: rgba(255, 255, 255, 1);
	border-color: #3b82f6;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.text-pattern-overlay.selected {
	background: rgba(59, 130, 246, 0.1);
	border-color: #3b82f6;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.text-pattern-overlay.dragging {
	background: rgba(59, 130, 246, 0.2);
	border-color: #3b82f6;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
	cursor: grabbing !important;
	z-index: 20;
}

.text-pattern-overlay.legacy-coordinate-pattern {
	/* Preview-only GD baseline correction; persisted coordinates stay unchanged. */
	transform: translateY(-1.35em);
}

.pattern-drag-handle {
	position: absolute;
	top: -9px;
	right: -9px;
	width: 18px;
	height: 18px;
	background: #3b82f6;
	border-radius: 3px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	cursor: grab;
	opacity: 0;
	transition: all 0.2s ease;
	font-size: 10px;
	flex-shrink: 0;
	z-index: 10;
}

.pattern-drag-handle:hover {
	background: #2563eb;
	transform: scale(1.1);
}

.pattern-drag-handle:active {
	cursor: grabbing;
	transform: scale(0.95);
}

.pattern-drag-handle.visible {
	opacity: 1;
}

.pattern-text-content {
	flex: 1;
	pointer-events: none;
}

.pattern-qr-preview {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	min-height: 32px;
	background: #fff;
	border: 2px solid #111827;
	color: #111827;
	font-size: 70%;
	line-height: 1;
	pointer-events: none;
}

.pattern-list {
	max-height: 200px;
	overflow-y: auto;
}

.pattern-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	margin-bottom: 6px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.pattern-item:hover {
	background: #f8fafc;
	border-color: #d1d5db;
}

.pattern-item.selected {
	background: rgba(59, 130, 246, 0.1);
	border-color: #3b82f6;
}

.pattern-text {
	flex: 1;
	font-size: 14px;
	color: #374151;
	font-weight: 500;
}

.pattern-actions {
	display: flex;
	gap: 4px;
}

.pattern-styling {
	padding: 16px;
	background: #f8fafc;
	border-radius: 8px;
	border: 1px solid #e5e7eb;
}

.pattern-styling h6 {
	color: #374151;
	font-weight: 600;
	margin-bottom: 16px;
}

.form-range {
	width: 100%;
}

.form-control-color {
	width: 50px;
	height: 38px;
	padding: 4px;
}

/* Fix input group button alignment */
.pattern-section .input-group {
	display: flex;
	width: 100%;
}

.pattern-section .input-group .form-control {
	flex: 1;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	border-right: 0;
}

.pattern-section .input-group .btn {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	flex-shrink: 0;
	white-space: nowrap;
}

/* Content Type Selection Styles */
.letter-content-section {
	margin-bottom: 24px;
}

.content-type-selection .btn-check:checked + .btn {
	background-color: #335cff;
	border-color: #335cff;
	color: white;
}

.content-type-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	border: 2px dashed #e2e8f0;
	border-radius: 12px;
	background: #f8fafc;
	text-align: center;
}

.content-type-placeholder .arrow-icon {
	color: #cbd5e1;
	margin-bottom: 12px;
}

.content-type-placeholder .placeholder-message {
	color: #64748b;
	margin: 0;
}

/* HTML Editor Modal Styles */
.letter-html-section {
	margin-bottom: 24px;
}

.html-preview-wrapper {
	border: 2px dashed #e2e8f0;
	border-radius: 12px;
	background: #f8fafc;
	padding: 24px;
	text-align: center;
}

.html-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
}

.html-viewer-container {
	width: 100%;
	max-height: 400px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow-y: auto;
	background: white;
	padding: 16px;
	text-align: left;
}

.html-content {
	font-family: 'Times New Roman', serif;
	line-height: 1.6;
	color: #374151;
}

.html-info {
	padding: 8px 12px;
	background: #f3f4f6;
	border-radius: 6px;
	margin: 8px 0;
}

.content-length {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
}

.html-actions {
	display: flex;
	gap: 8px;
	justify-content: center;
}

.html-create-placeholder {
	cursor: pointer;
	transition: all 0.2s ease;
	padding: 40px 20px;
}

.html-create-placeholder:hover {
	background: #f1f5f9;
	border-color: #cbd5e1;
	transform: translateY(-1px);
}

/* HTML Editor Content */
.html-editor-container {
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	background: white;
}

.editor-toolbar {
	padding: 12px;
	background: #f8fafc;
	border-bottom: 1px solid #e5e7eb;
	border-radius: 8px 8px 0 0;
}

.html-editor-content {
	min-height: 400px;
	padding: 16px;
	font-family: 'Times New Roman', serif;
	font-size: 14px;
	line-height: 1.6;
	border: none;
	outline: none;
	overflow-y: auto;
}

.html-editor-content:focus {
	outline: 2px solid #3b82f6;
	outline-offset: -2px;
}

.editor-footer {
	padding: 8px 16px;
	background: #f8fafc;
	border-top: 1px solid #e5e7eb;
	border-radius: 0 0 8px 8px;
}

/* Template Variables Panel */
.template-variables {
	background: #f8fafc;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 16px;
	max-height: 600px;
	overflow-y: auto;
}

.variables-help {
	font-size: 13px;
	color: #6b7280;
	margin-bottom: 12px;
}

.variable-category {
	margin-bottom: 16px;
}

.category-title {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 6px;
	padding-bottom: 3px;
	border-bottom: 1px solid #e5e7eb;
}

.variable-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.variable-tag {
	display: inline-block;
	font-family: 'Courier New', monospace;
	font-size: 10px;
	padding: 3px 6px;
	background: #e5e7eb;
	color: #374151;
	border-radius: 3px;
	cursor: pointer;
	transition: all 0.2s ease;
	white-space: nowrap;
	user-select: none;
}

.variable-tag:hover {
	background: #335cff;
	color: white;
	transform: scale(1.05);
}

/* TinyMCE Variables Panel Styling */
.variables-panel {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.variables-title {
	font-size: 16px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 8px;
	padding-bottom: 8px;
	border-bottom: 2px solid #e5e7eb;
}

.variables-help {
	color: #6b7280;
	font-size: 14px;
	margin-bottom: 16px;
}

.variables-list-wrapper {
	flex: 1;
	max-height: 350px;
	overflow-y: auto;
	padding-right: 8px;
}

.variable-item {
	display: block;
	width: 100%;
	padding: 8px 12px;
	margin-bottom: 6px;
	background: #f8fafc;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	color: #374151;
	font-family: 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
	font-size: 12px;
	cursor: pointer;
	transition: all 0.2s ease;
	user-select: none;
	word-break: break-all;
}

.variable-item:hover {
	background: #335cff;
	color: white;
	border-color: #335cff;
	transform: translateX(4px);
	box-shadow: 0 2px 4px rgba(51, 92, 255, 0.2);
}

.variable-item:active {
	transform: translateX(4px) scale(0.98);
}

.variables-help-note {
	margin-top: 16px;
	padding: 12px;
	background: #f0f9ff;
	border: 1px solid #bae6fd;
	border-radius: 6px;
	display: flex;
	align-items: flex-start;
	gap: 8px;
}

.variables-list-wrapper::-webkit-scrollbar {
	width: 6px;
}

.variables-list-wrapper::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 3px;
}

.variables-list-wrapper::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

.variables-list-wrapper::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

/* TinyMCE Modal Styling */
#htmlEditorModal .modal-body {
	padding: 24px;
}

#htmlEditorModal .tox-tinymce {
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
}

/* PDF Structure Styling in TinyMCE */
#htmlEditorModal .tox-edit-area .pdf-page {
	margin-bottom: 30px;
	padding: 20px;
	border: 2px solid #e5e7eb;
	border-radius: 8px;
	background: #fafbfc;
	position: relative;
}

#htmlEditorModal .tox-edit-area .pdf-page::before {
	content: 'PDF Page ' attr(data-page);
	position: absolute;
	top: -12px;
	left: 20px;
	background: #335cff;
	color: white;
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
}

#htmlEditorModal .tox-edit-area .page-header {
	color: #1f2937;
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 16px 0;
	padding-bottom: 8px;
	border-bottom: 1px solid #d1d5db;
}

#htmlEditorModal .tox-edit-area .editable-content {
	margin-bottom: 12px;
	padding: 12px;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	transition: all 0.2s ease;
	position: relative;
}

#htmlEditorModal .tox-edit-area .editable-content:hover {
	border-color: #335cff;
	box-shadow: 0 2px 4px rgba(51, 92, 255, 0.1);
}

#htmlEditorModal .tox-edit-area .editable-content::before {
	content: 'P' attr(data-paragraph);
	position: absolute;
	top: -8px;
	right: 8px;
	background: #f3f4f6;
	color: #6b7280;
	padding: 2px 6px;
	border-radius: 3px;
	font-size: 9px;
	font-weight: 600;
	opacity: 0;
	transition: opacity 0.2s ease;
}

#htmlEditorModal .tox-edit-area .editable-content:hover::before {
	opacity: 1;
}

/* Enhanced Modal Footer */
.editor-modal-footer {
	background: #f8fafc;
	border-top: 2px solid #e5e7eb;
	padding: 16px 24px;
}

.footer-help {
	display: flex;
	align-items: center;
}

.footer-actions {
	display: flex;
	align-items: center;
}

/* Match the Customizer AI helper's flat gh-style modal language. */
#templateSettingsModal .modal-content { border: 1px solid #e6e8ec; border-radius: 14px; overflow: hidden; box-shadow: 0 18px 55px rgba(31, 41, 55, 0.16); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
#templateSettingsModal .pattern-modal-header { border-bottom: 1px solid #f0f1f4; padding: 0.9rem 1.25rem; background: #fff; }
#templateSettingsModal .pattern-modal-title { display: flex; align-items: center; font-size: 1rem; font-weight: 700; color: #2b303b; letter-spacing: -0.01em; margin-bottom: 2px; }
#templateSettingsModal .pattern-modal-sub { display: block; font-size: 0.78rem; color: #8a909c; line-height: 1.45; }
#templateSettingsModal .pattern-modal-close { margin-left: auto; width: 30px; height: 30px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 7px; background: transparent; color: #2b303b; font-size: 24px; line-height: 1; opacity: 0.65; }
#templateSettingsModal .pattern-modal-close:hover { background: #f3f5f9; color: #171a20; opacity: 1; }
#templateSettingsModal .template-image-main-container { background: #f3f5f9; border-right: 1px solid #e6e8ec; }
#templateSettingsModal .template-image-scroll-container { padding: 16px; }
#templateSettingsModal .template-image-container { border: 1px solid #e6e8ec; border-radius: 12px; box-shadow: 0 8px 24px rgba(43, 48, 59, 0.1); overflow: hidden; }
#templateSettingsModal .template-sidebar { padding: 18px; border-left: 0; }
#templateSettingsModal .template-sidebar { overflow-y: auto; overflow-x: hidden; scrollbar-width: thin; scrollbar-color: transparent transparent; }
#templateSettingsModal .template-sidebar:hover { scrollbar-color: #aeb4bf transparent; }
#templateSettingsModal .template-sidebar::-webkit-scrollbar { width: 5px; height: 0; }
#templateSettingsModal .template-sidebar::-webkit-scrollbar-track { background: transparent; }
#templateSettingsModal .template-sidebar::-webkit-scrollbar-thumb { background: transparent; border-radius: 999px; }
#templateSettingsModal .template-sidebar:hover::-webkit-scrollbar-thumb { background: #aeb4bf; }
#templateSettingsModal .pattern-sidebar-heading { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #8a909c; }
#templateSettingsModal .pattern-sidebar-sub { margin: 4px 0 16px; font-size: 12px; color: #8a909c; line-height: 1.45; }
#templateSettingsModal .form-label { font-size: 0.78rem; font-weight: 650; color: #2b303b; }
#templateSettingsModal .form-control, #templateSettingsModal .form-select { border-color: #e6e8ec; border-radius: 8px; font-size: 0.8rem; box-shadow: none; }
#templateSettingsModal .form-control:focus, #templateSettingsModal .form-select:focus { border-color: #2b6cff; box-shadow: 0 0 0 2px rgba(43, 108, 255, 0.1); }
#templateSettingsModal .input-group-btn { border-radius: 0 8px 8px 0; background: #2b303b; border-color: #2b303b; }
#templateSettingsModal .placement-hint { background: #f3f5f9; color: #596170; border-radius: 10px; }
#templateSettingsModal .variable-helper { border-color: #e6e8ec; background: transparent; border-radius: 12px; }
#templateSettingsModal .variable-chip { border-color: #e6e8ec; border-radius: 10px; }
#templateSettingsModal .variable-chip:hover { background: #f3f5f9; border-color: #d6dae2; }
#templateSettingsModal .variable-chip code { color: #2b303b; }
#templateSettingsModal .pattern-item { border-color: #e6e8ec; border-radius: 10px; }
#templateSettingsModal .pattern-item:hover, #templateSettingsModal .pattern-item.selected { background: #f3f5f9; border-color: #d6dae2; }
#templateSettingsModal .sidebar-footer { background: #fff; border-top: 1px solid #f0f1f4 !important; }
#templateSettingsModal .pattern-modal-btn { display: inline-flex; align-items: center; justify-content: center; padding: 0.42rem 0.7rem; background: transparent; border: 1px solid #e6e8ec; border-radius: 7px; color: #2b303b; font-size: 0.78rem; font-weight: 600; }
#templateSettingsModal .pattern-modal-btn:hover { background: #f3f5f9; border-color: #d6dae2; }
#templateSettingsModal .pattern-modal-btn--primary { background: #2b303b; border-color: #2b303b; color: #fff; }
#templateSettingsModal .pattern-modal-btn--primary:hover { background: #171a20; border-color: #171a20; }
#templateSettingsModal .template-image-scroll-container { align-items: center; justify-content: center; overflow: auto; }
#templateSettingsModal .template-image-container { display: inline-block; width: fit-content; max-width: 100%; max-height: 100%; flex: 0 0 auto; }
#templateSettingsModal .template-preview-image { display: block; width: auto; height: auto; max-width: 100%; max-height: calc(95vh - 112px); min-width: 0; object-fit: contain; }
.unique-url-section { border-color: #dbe5ff; background: #fbfcff; }
</style>
